import { Suspense, lazy } from "react";
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes, useParams } from "react-router-dom";

/**
 * Redireciona rotas antigas do e-Learning para o V2 preservando os
 * params da rota original. Usado enquanto os endpoints legados vivem.
 */
function RedirectLegacyElearning({ template }: { template: string }) {
  const params = useParams();
  const to = template.replace(/:(\w+)/g, (_, k) => String(params[k] ?? ""));
  return <Navigate to={to} replace />;
}
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AlunoGuard } from "./components/guards/AlunoGuard";
import { ResponsavelGuard } from "./components/guards/ResponsavelGuard";
import { PageSkeleton } from "./components/ui/page-skeleton";

// Lazy imports - Páginas públicas
const MapaEscolasPage = lazy(() =>
  import("./pages/public/mapa/MapaEscolasPage").then((m) => ({
    default: m.MapaEscolasPage,
  }))
);
const MatriculaPublicaWizardPage = lazy(() =>
  import("./pages/public/matricula/MatriculaPublicaWizardPage").then((m) => ({
    default: m.MatriculaPublicaWizardPage,
  }))
);
const MatriculaPublicaSuccessPage = lazy(() =>
  import("./pages/public/matricula/MatriculaPublicaSuccessPage").then((m) => ({
    default: m.MatriculaPublicaSuccessPage,
  }))
);
const ConsultaProtocoloPage = lazy(() =>
  import("./pages/public/matricula/ConsultaProtocoloPage").then((m) => ({
    default: m.ConsultaProtocoloPage,
  }))
);

// Lazy imports utilitários (mapeando exports nomeados para default quando necessário)
const LoginPage = lazy(() =>
  import("./pages/auth/LoginPageNew").then((m) => ({ default: m.LoginPageNew }))
);
const RegisterPage = lazy(() =>
  import("./pages/auth/RegisterPage").then((m) => ({ default: m.RegisterPage }))
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/auth/ForgotPasswordPageNew").then((m) => ({
    default: m.ForgotPasswordPageNew,
  }))
);
const ResetPasswordPage = lazy(() =>
  import("./pages/auth/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  }))
);
const DashboardPage = lazy(() =>
  import("./pages/dashboard/DashboardPage").then((m) => ({
    default: m.DashboardPage,
  }))
);
const TeacherDashboardPage = lazy(() =>
  import("./pages/dashboard/TeacherDashboardPage").then((m) => ({
    default: m.TeacherDashboardPage,
  }))
);
const SmartDashboard = lazy(() =>
  import("./pages/dashboard/SmartDashboard").then((m) => ({
    default: m.SmartDashboard,
  }))
);
const SecretariaFormPage = lazy(() =>
  import("./pages/secretarias/SecretariaFormPage").then((m) => ({
    default: m.SecretariaFormPage,
  }))
);
const SecretariaListPage = lazy(() =>
  import("./pages/secretarias/SecretariaListPage").then((m) => ({
    default: m.SecretariaListPage,
  }))
);
const SecretariaViewPage = lazy(() =>
  import("./pages/secretarias/SecretariaViewPage").then((m) => ({
    default: m.SecretariaViewPage,
  }))
);
const UserProfilePage = lazy(() =>
  import("./pages/user/UserProfilePage").then((m) => ({
    default: m.UserProfilePage,
  }))
);
const Layout = lazy(() =>
  import("./components/Layout").then((m) => ({ default: m.Layout }))
);
const PermissaoDashboardPage = lazy(() =>
  import("./pages/permissoes/PermissaoDashboardPage").then((m) => ({
    default: m.PermissaoDashboardPage,
  }))
);
const PermissaoListPage = lazy(() =>
  import("./pages/permissoes/PermissaoListPage").then((m) => ({
    default: m.PermissaoListPage,
  }))
);
const RoleCreatePage = lazy(() =>
  import("./pages/roles/RoleCreatePage").then((m) => ({
    default: m.RoleCreatePage,
  }))
);
const RoleEditPage = lazy(() =>
  import("./pages/roles/RoleEditPage").then((m) => ({
    default: m.RoleEditPage,
  }))
);
const RoleListPage = lazy(() =>
  import("./pages/roles/RoleListPage").then((m) => ({
    default: m.RoleListPage,
  }))
);
const RoleViewPage = lazy(() =>
  import("./pages/roles/RoleViewPage").then((m) => ({
    default: m.RoleViewPage,
  }))
);
const VinculoFormPage = lazy(() =>
  import("./pages/vinculos/VinculoFormPage").then((m) => ({
    default: m.VinculoFormPage,
  }))
);
const VinculoListPage = lazy(() =>
  import("./pages/vinculos/VinculoListPage").then((m) => ({
    default: m.VinculoListPage,
  }))
);
const VinculoViewPage = lazy(() =>
  import("./pages/vinculos/VinculoViewPage").then((m) => ({
    default: m.VinculoViewPage,
  }))
);
const RedirectToLogin = lazy(() =>
  import("./components/RedirectToLogin").then((m) => ({
    default: m.RedirectToLogin,
  }))
);
// Removed outdated aluno imports - now integrated into pessoas module
const EscolaFormPage = lazy(() =>
  import("./pages/escolas/EscolaFormPage").then((m) => ({
    default: m.EscolaFormPage,
  }))
);
const EscolaListPage = lazy(() =>
  import("./pages/escolas/EscolaListPage").then((m) => ({
    default: m.EscolaListPage,
  }))
);
const EscolaViewPage = lazy(() =>
  import("./pages/escolas/EscolaViewPage").then((m) => ({
    default: m.EscolaViewPage,
  }))
);
const MatriculaFormPage = lazy(() =>
  import("./pages/matriculas/MatriculaFormPage").then((m) => ({
    default: m.MatriculaFormPage,
  }))
);

// Pre-Matricula - Admin
const PreMatriculaAnalisePage = lazy(() =>
  import("./pages/matriculas/pre-matricula/PreMatriculaAnalisePage").then((m) => ({
    default: m.PreMatriculaAnalisePage,
  }))
);
const PreMatriculaEfetivacaoPage = lazy(() =>
  import("./pages/matriculas/pre-matricula/PreMatriculaEfetivacaoPage").then((m) => ({
    default: m.PreMatriculaEfetivacaoPage,
  }))
);
const PreMatriculaVagasPage = lazy(() =>
  import("./pages/matriculas/pre-matricula/PreMatriculaVagasPage").then((m) => ({
    default: m.PreMatriculaVagasPage,
  }))
);
const MatriculaListPage = lazy(() =>
  import("./pages/matriculas/MatriculaListPage").then((m) => ({
    default: m.MatriculaListPage,
  }))
);
const MatriculaViewPage = lazy(() =>
  import("./pages/matriculas/MatriculaViewPage").then((m) => ({
    default: m.MatriculaViewPage,
  }))
);
const RelatorioImportadoListPage = lazy(() =>
  import("./pages/relatorio-importado/RelatorioImportadoListPage").then((m) => ({
    default: m.RelatorioImportadoListPage,
  }))
);
const RelatorioImportadoEditorPage = lazy(() =>
  import("./pages/relatorio-importado/RelatorioImportadoEditorPage").then((m) => ({
    default: m.RelatorioImportadoEditorPage,
  }))
);
const InviteCreatePage = lazy(() =>
  import("./pages/invites/InviteCreatePage").then((m) => ({
    default: m.InviteCreatePage,
  }))
);
const InviteEditPage = lazy(() =>
  import("./pages/invites/InviteEditPage").then((m) => ({
    default: m.InviteEditPage,
  }))
);
const InviteListPage = lazy(() =>
  import("./pages/invites/InviteListPage").then((m) => ({
    default: m.InviteListPage,
  }))
);
const InviteViewPage = lazy(() =>
  import("./pages/invites/InviteViewPage").then((m) => ({
    default: m.InviteViewPage,
  }))
);
const ComunicadoListPage = lazy(() =>
  import("./pages/comunicados/ComunicadoListPage").then((m) => ({
    default: m.ComunicadoListPage,
  }))
);
const ComunicadoFormPage = lazy(() =>
  import("./pages/comunicados/ComunicadoFormPage").then((m) => ({
    default: m.ComunicadoFormPage,
  }))
);
const SuporteInternoPage = lazy(() =>
  import("./pages/suporte-interno/SuporteInternoPage").then((m) => ({
    default: m.SuporteInternoPage,
  }))
);
const NotificacoesPage = lazy(() =>
  import("./pages/notificacoes/NotificacoesPage").then((m) => ({
    default: m.NotificacoesPage,
  }))
);
const AnosLetivosPage = lazy(
  () => import("./pages/estrutura-academica/anos-letivos")
);
const AnoLetivoViewPage = lazy(
  () => import("./pages/estrutura-academica/anos-letivos/AnoLetivoViewPage")
);
const UserListPage = lazy(() =>
  import("./pages/users/UserListPage").then((m) => ({
    default: m.UserListPage,
  }))
);
const UserDetailPage = lazy(() =>
  import("./pages/users/UserDetailPage").then((m) => ({
    default: m.UserDetailPage,
  }))
);
const AlocacaoPage = lazy(() =>
  import("./pages/academico/alocacao-pedagogica/AlocacaoPage").then((m) => ({
    default: m.AlocacaoPage,
  }))
);
const CursoListPage = lazy(() =>
  import("./pages/academico/cursos/CursoListPageNew").then((m) => ({
    default: m.CursoListPageNew,
  }))
);
const CursoDetailPage = lazy(() =>
  import("./pages/academico/cursos/CursoDetailPageNew").then((m) => ({
    default: m.CursoDetailPageNew,
  }))
);
const DisciplinaListPage = lazy(() =>
  import("./pages/academico/disciplinas/DisciplinaListPage").then((m) => ({
    default: m.DisciplinaListPage,
  }))
);
const DisciplinaDetailPage = lazy(() =>
  import("./pages/academico/disciplinas/DisciplinaDetailPage").then((m) => ({
    default: m.DisciplinaDetailPage,
  }))
);
const GradeHorariaPage = lazy(() =>
  import("./pages/academico/grade-horaria/GradeHorariaPage").then((m) => ({
    default: m.GradeHorariaPage,
  }))
);
const TurnosPage = lazy(() =>
  import("./pages/academico/turnos/TurnosPage").then((m) => ({
    default: m.TurnosPage,
  }))
);
const PeriodosPage = lazy(() =>
  import("./pages/academico/turnos/periodos/PeriodosPage").then((m) => ({
    default: m.PeriodosPage,
  }))
);
const DiarioIndexPage = lazy(
  () => import("./pages/academico/diario/DiarioIndexPage")
);
const TipoAvaliacaoListPage = lazy(() =>
  import("./pages/academico/tipos-avaliacao/TipoAvaliacaoListPage").then(
    (m) => ({
      default: m.TipoAvaliacaoListPage,
    })
  )
);
const MatrizCurricularPage = lazy(() =>
  import("./pages/academico/matrizes-curriculares/MatrizCurricularPage").then(
    (m) => ({ default: m.MatrizCurricularPage })
  )
);
const BNCCIndexPage = lazy(() =>
  import("./pages/academico/bncc/BNCCIndexPage").then((m) => ({
    default: m.BNCCIndexPage,
  }))
);
const HabilidadesGestaoPage = lazy(() =>
  import("./pages/academico/bncc/habilidades/HabilidadesGestaoPage").then(
    (m) => ({
      default: m.HabilidadesGestaoPage,
    })
  )
);
const EtapasGestaoPage = lazy(() =>
  import("./pages/academico/bncc/etapas/EtapasGestaoPage").then((m) => ({
    default: m.EtapasGestaoPage,
  }))
);
const AreasGestaoPage = lazy(() =>
  import("./pages/academico/bncc/areas/AreasGestaoPage").then((m) => ({
    default: m.AreasGestaoPage,
  }))
);
const ComponentesGestaoPage = lazy(() =>
  import("./pages/academico/bncc/componentes/ComponentesGestaoPage").then(
    (m) => ({
      default: m.ComponentesGestaoPage,
    })
  )
);
const UnidadesTematicasGestaoPage = lazy(() =>
  import(
    "./pages/academico/bncc/unidades-tematicas/UnidadesTematicasGestaoPage"
  ).then((m) => ({
    default: m.UnidadesTematicasGestaoPage,
  }))
);
const ObjetosConhecimentoGestaoPage = lazy(() =>
  import(
    "./pages/academico/bncc/objetos-conhecimento/ObjetosConhecimentoGestaoPage"
  ).then((m) => ({
    default: m.ObjetosConhecimentoGestaoPage,
  }))
);
const CompetenciasEspecificasGestaoPage = lazy(() =>
  import(
    "./pages/academico/bncc/competencias-especificas/CompetenciasEspecificasGestaoPage"
  ).then((m) => ({
    default: m.CompetenciasEspecificasGestaoPage,
  }))
);
const CamposExperienciaGestaoPage = lazy(() =>
  import(
    "./pages/academico/bncc/campos-experiencia/CamposExperienciaGestaoPage"
  ).then((m) => ({
    default: m.CamposExperienciaGestaoPage,
  }))
);
const ObjetivosAprendizagemGestaoPage = lazy(() =>
  import(
    "./pages/academico/bncc/objetivos-aprendizagem/ObjetivosAprendizagemGestaoPage"
  ).then((m) => ({
    default: m.ObjetivosAprendizagemGestaoPage,
  }))
);
const TurmaFormPage = lazy(() =>
  import("./pages/turmas/TurmaFormPage").then((m) => ({
    default: m.TurmaFormPage,
  }))
);
const TurmaListPage = lazy(() =>
  import("./pages/turmas/TurmaListPage").then((m) => ({
    default: m.TurmaListPage,
  }))
);
const TurmaViewPage = lazy(() =>
  import("./pages/turmas/TurmaViewPage").then((m) => ({
    default: m.TurmaViewPage,
  }))
);
const MinhasTurmasPage = lazy(() =>
  import("./pages/turmas/MinhasTurmasPage").then((m) => ({
    default: m.MinhasTurmasPage,
  }))
);
const TeacherClassView = lazy(() => import("./pages/turmas/TeacherClassView"));
const FrequenciaTurmaPage = lazy(() =>
  import("./pages/turmas/FrequenciaTurmaPage").then((m) => ({
    default: m.default,
  }))
);
const PlanoAulaListPage = lazy(() =>
  import("./pages/turmas/planos-aula/PlanoAulaListPage").then((m) => ({
    default: m.default,
  }))
);
const PlanoAulaFormPage = lazy(() =>
  import("./pages/turmas/planos-aula/PlanoAulaFormPage").then((m) => ({
    default: m.default,
  }))
);
const PlanoAulaViewPage = lazy(() =>
  import("./pages/turmas/planos-aula/PlanoAulaViewPage").then((m) => ({
    default: m.default,
  }))
);
const SalaListPage = lazy(() => import("./pages/salas/SalaListPage"));

// Audiência de Leitura - lazy
const AudienciaListPage = lazy(() =>
  import("./components/audiencia-leitura/pages/AudienciaListPage").then((m) => ({
    default: m.AudienciaListPage,
  }))
);
const AudienciaFormPage = lazy(() =>
  import("./components/audiencia-leitura/pages/AudienciaFormPage").then((m) => ({
    default: m.AudienciaFormPage,
  }))
);
const AudienciaViewPage = lazy(() =>
  import("./components/audiencia-leitura/pages/AudienciaViewPage").then((m) => ({
    default: m.AudienciaViewPage,
  }))
);

// Carteira Estudantil
const CarteiraEstudantilListPage = lazy(() =>
  import("./pages/carteira-estudantil/CarteiraEstudantilListPage").then((m) => ({
    default: m.CarteiraEstudantilListPage,
  }))
);
const CarteiraEstudantilViewPage = lazy(() =>
  import("./pages/carteira-estudantil/CarteiraEstudantilViewPage").then((m) => ({
    default: m.CarteiraEstudantilViewPage,
  }))
);

// Pessoas - lazy
const PessoaListPage = lazy(() => import("./pages/pessoas/PessoaListPage"));
const PessoaWizardPage = lazy(() => import("./pages/pessoas/PessoaWizardPage"));
const FuncionarioListPage = lazy(
  () => import("./pages/pessoas/FuncionarioListPage")
);
const AlunoListPageNew = lazy(() => import("./pages/pessoas/AlunoListPage"));
const AlunoDetailPage = lazy(() => import("./pages/pessoas/AlunoDetailPage"));
const FuncionarioDetailPage = lazy(() => import("./pages/pessoas/FuncionarioDetailPage"));
const CargoListPage = lazy(() => import("./pages/pessoas/CargoListPage"));
const CargoFormPage = lazy(() => import("./pages/pessoas/CargoFormPage"));
const CargoViewPage = lazy(() => import("./pages/pessoas/CargoViewPage"));

// Busca Ativa
const BuscaAtivaListPage = lazy(() =>
  import("./pages/busca-ativa/BuscaAtivaListPage").then((m) => ({
    default: m.BuscaAtivaListPage,
  }))
);
const BuscaAtivaViewPage = lazy(() =>
  import("./pages/busca-ativa/BuscaAtivaViewPage").then((m) => ({
    default: m.BuscaAtivaViewPage,
  }))
);

// GED - Gestão Eletrônica de Documentos
const GEDDashboardPage = lazy(() =>
  import("./pages/ged/GEDDashboardPage").then((m) => ({
    default: m.GEDDashboardPage,
  }))
);
const GEDBuscaPage = lazy(() =>
  import("./pages/ged/GEDBuscaPage").then((m) => ({
    default: m.GEDBuscaPage,
  }))
);
const GEDPastasPage = lazy(() =>
  import("./pages/ged/GEDPastasPage").then((m) => ({
    default: m.GEDPastasPage,
  }))
);
const GEDTiposPage = lazy(() =>
  import("./pages/ged/GEDTiposPage").then((m) => ({
    default: m.GEDTiposPage,
  }))
);
const GEDDocumentoDetailPage = lazy(() =>
  import("./pages/ged/GEDDocumentoDetailPage").then((m) => ({
    default: m.GEDDocumentoDetailPage,
  }))
);

// Termo de Autorização
const TermoAutorizacaoListPage = lazy(() =>
  import("./pages/termo-autorizacao/TermoAutorizacaoListPage").then((m) => ({
    default: m.TermoAutorizacaoListPage,
  }))
);

// Campanhas
const CampanhaListPage = lazy(() =>
  import("./pages/campanhas/CampanhaListPage").then((m) => ({
    default: m.CampanhaListPage,
  }))
);
const CampanhaFormPage = lazy(() =>
  import("./pages/campanhas/CampanhaFormPage").then((m) => ({
    default: m.CampanhaFormPage,
  }))
);
const CampanhaViewPage = lazy(() =>
  import("./pages/campanhas/CampanhaViewPage").then((m) => ({
    default: m.CampanhaViewPage,
  }))
);
const QuestionarioFormPage = lazy(() =>
  import("./pages/campanhas/QuestionarioFormPage").then((m) => ({
    default: m.QuestionarioFormPage,
  }))
);

// Descritores
const DescritoresListPage = lazy(() =>
  import("./components/descritores/pages/DescritoresListPage").then((m) => ({
    default: m.DescritoresListPage,
  }))
);
const DescritorFormPage = lazy(() =>
  import("./components/descritores/pages/DescritorFormPage").then((m) => ({
    default: m.DescritorFormPage,
  }))
);
const DescritorViewPage = lazy(() =>
  import("./components/descritores/pages/DescritorViewPage").then((m) => ({
    default: m.DescritorViewPage,
  }))
);

const ResponderQuestionarioPage = lazy(() =>
  import("./pages/campanhas/ResponderQuestionarioPage").then((m) => ({
    default: m.ResponderQuestionarioPage,
  }))
);
const ResultadosPage = lazy(() =>
  import("./pages/campanhas/ResultadosPage").then((m) => ({
    default: m.ResultadosPage,
  }))
);
const AnaliseComparativaPage = lazy(() =>
  import("./pages/campanhas/AnaliseComparativaPage").then((m) => ({
    default: m.AnaliseComparativaPage,
  }))
);
const AlertasPage = lazy(() =>
  import("./pages/campanhas/AlertasPage").then((m) => ({
    default: m.AlertasPage,
  }))
);
const AuditoriaColetasPage = lazy(() =>
  import("./pages/campanhas/AuditoriaColetasPage").then((m) => ({
    default: m.AuditoriaColetasPage,
  }))
);
const PesquisaPublicaPage = lazy(() =>
  import("./pages/public/pesquisa/PesquisaPublicaPage").then((m) => ({
    default: m.PesquisaPublicaPage,
  }))
);
const PrevisaoEvasaoPage = lazy(() =>
  import("./pages/previsao-evasao/PrevisaoEvasaoPage").then((m) => ({
    default: m.PrevisaoEvasaoPage,
  }))
);
// V2 · Área do Aluno
const AlunoCatalogoPage = lazy(() =>
  import("./pages/elearning/aluno/CatalogoPage").then((m) => ({
    default: m.CatalogoPage,
  }))
);
const AlunoInscricaoPlayerPage = lazy(() =>
  import("./pages/elearning/aluno/InscricaoPlayerPage").then((m) => ({
    default: m.InscricaoPlayerPage,
  }))
);
const AlunoTrilhaCatalogoPage = lazy(() =>
  import("./pages/elearning/aluno/TrilhaCatalogoPage").then((m) => ({
    default: m.TrilhaCatalogoPage,
  }))
);
const AlunoTrilhaDetailPage = lazy(() =>
  import("./pages/elearning/aluno/TrilhaDetailPage").then((m) => ({
    default: m.TrilhaDetailPage,
  }))
);
const AlunoMinhasObrigacoesPage = lazy(() =>
  import("./pages/elearning/aluno/MinhasObrigacoesPage").then((m) => ({
    default: m.MinhasObrigacoesPage,
  }))
);
const AlunoMeusCertificadosPage = lazy(() =>
  import("./pages/elearning/aluno/MeusCertificadosPage").then((m) => ({
    default: m.MeusCertificadosPage,
  }))
);
// V2 · Área de Autoria
const AutoriaMeusCursosPage = lazy(() =>
  import("./pages/elearning/autoria/MeusCursosPage").then((m) => ({
    default: m.MeusCursosPage,
  }))
);
const AutoriaCursoEditorPage = lazy(() =>
  import("./pages/elearning/autoria/CursoEditorPage").then((m) => ({
    default: m.CursoEditorPage,
  }))
);
const AutoriaCursoAlunosPage = lazy(() =>
  import("./pages/elearning/autoria/CursoAlunosPage").then((m) => ({
    default: m.CursoAlunosPage,
  }))
);
// V2 · Área de Gestão
const GestaoDashboardPage = lazy(() =>
  import("./pages/elearning/gestao/DashboardPage").then((m) => ({
    default: m.DashboardPage,
  }))
);
const GestaoCategoriasPage = lazy(() =>
  import("./pages/elearning/gestao/CategoriasPage").then((m) => ({
    default: m.CategoriasPage,
  }))
);
const GestaoComplianceUnificadoPage = lazy(() =>
  import("./pages/elearning/gestao/ComplianceUnificadoPage").then((m) => ({
    default: m.ComplianceUnificadoPage,
  }))
);
const GestaoTrilhasAdminPage = lazy(() =>
  import("./pages/elearning/gestao/TrilhasAdminPage").then((m) => ({
    default: m.TrilhasAdminPage,
  }))
);
const GestaoTemplateCertificadoPage = lazy(() =>
  import("./pages/elearning/gestao/TemplateCertificadoPage").then((m) => ({
    default: m.TemplateCertificadoPage,
  }))
);
const CertificadoPublicoPage = lazy(() =>
  import("./pages/public/certificado/CertificadoPublicoPage").then((m) => ({
    default: m.CertificadoPublicoPage,
  }))
);
const CertificadoTrilhaPublicoPage = lazy(() =>
  import("./pages/public/certificado/CertificadoTrilhaPublicoPage").then((m) => ({
    default: m.CertificadoTrilhaPublicoPage,
  }))
);
const CertificadoFormacaoPublicoPage = lazy(() =>
  import("./pages/public/certificado/CertificadoFormacaoPublicoPage").then((m) => ({
    default: m.CertificadoFormacaoPublicoPage,
  }))
);
const DiagnosticoAlfabetizacaoPage = lazy(() =>
  import("./pages/alfabetizacao/DiagnosticoAlfabetizacaoPage").then((m) => ({
    default: m.DiagnosticoAlfabetizacaoPage,
  }))
);
const AcompanhamentoPedagogicoPage = lazy(() =>
  import("./pages/acompanhamento-pedagogico/AcompanhamentoPedagogicoPage").then((m) => ({
    default: m.AcompanhamentoPedagogicoPage,
  }))
);
const AvancoEstudosPage = lazy(() =>
  import("./pages/avanco-estudos/AvancoEstudosPage").then((m) => ({
    default: m.AvancoEstudosPage,
  }))
);
const PeiPage = lazy(() =>
  import("./pages/pei/PeiPage").then((m) => ({
    default: m.PeiPage,
  }))
);
const AlunosDuplicadosPage = lazy(() =>
  import("./pages/alunos/AlunosDuplicadosPage").then((m) => ({
    default: m.AlunosDuplicadosPage,
  }))
);
const TransparenciaAdminPage = lazy(() =>
  import("./pages/transparencia/TransparenciaAdminPage").then((m) => ({
    default: m.TransparenciaAdminPage,
  }))
);
const TransparenciaPublicaPage = lazy(() =>
  import("./pages/public/transparencia/TransparenciaPublicaPage").then((m) => ({
    default: m.TransparenciaPublicaPage,
  }))
);
const DocumentoPublicoPage = lazy(() =>
  import("./pages/public/transparencia/DocumentoPublicoPage").then((m) => ({
    default: m.DocumentoPublicoPage,
  }))
);
const VerificarDocumentoPage = lazy(() =>
  import("./pages/public/validador/VerificarDocumentoPage").then((m) => ({
    default: m.VerificarDocumentoPage,
  }))
);

// Avaliações Estruturadas
const QuestoesListPage = lazy(() =>
  import("./components/avaliacoes/pages/QuestoesListPage").then((m) => ({
    default: m.QuestoesListPage,
  }))
);
const QuestaoFormPage = lazy(() =>
  import("./components/avaliacoes/pages/QuestaoFormPage").then((m) => ({
    default: m.QuestaoFormPage,
  }))
);
const QuestaoViewPage = lazy(() =>
  import("./components/avaliacoes/pages/QuestaoViewPage").then((m) => ({
    default: m.QuestaoViewPage,
  }))
);
const AvaliacoesListPage = lazy(() =>
  import("./components/avaliacoes/pages/AvaliacoesListPage").then((m) => ({
    default: m.AvaliacoesListPage,
  }))
);
const AvaliacaoFormPage = lazy(() =>
  import("./components/avaliacoes/pages/AvaliacaoFormPage").then((m) => ({
    default: m.AvaliacaoFormPage,
  }))
);
const AvaliacaoBuilderPage = lazy(() =>
  import("./components/avaliacoes/pages/AvaliacaoBuilderPage").then((m) => ({
    default: m.AvaliacaoBuilderPage,
  }))
);
const AvaliacaoViewPage = lazy(() =>
  import("./components/avaliacoes/pages/AvaliacaoViewPage").then((m) => ({
    default: m.AvaliacaoViewPage,
  }))
);
const AvaliacaoDashboardPage = lazy(() =>
  import("./components/avaliacoes/pages/AvaliacaoDashboardPage").then((m) => ({
    default: m.AvaliacaoDashboardPage,
  }))
);
const AvaliacaoOnlinePage = lazy(() =>
  import("./components/avaliacoes/pages/AvaliacaoOnlinePage").then((m) => ({
    default: m.AvaliacaoOnlinePage,
  }))
);
const CorrecaoPage = lazy(() =>
  import("./components/avaliacoes/pages/CorrecaoPage").then((m) => ({
    default: m.CorrecaoPage,
  }))
);
const ResultadosAvaliacaoPage = lazy(() =>
  import("./components/avaliacoes/pages/ResultadosAvaliacaoPage").then((m) => ({
    default: m.ResultadosAvaliacaoPage,
  }))
);

// Analytics de Avaliações
const DashboardSecretariaPage = lazy(() =>
  import("./pages/analytics/DashboardSecretariaPage").then((m) => ({
    default: m.DashboardSecretariaPage,
  }))
);
const DashboardEscolaPage = lazy(() =>
  import("./pages/analytics/DashboardEscolaPage").then((m) => ({
    default: m.DashboardEscolaPage,
  }))
);
const DashboardTurmaPage = lazy(() =>
  import("./pages/analytics/DashboardTurmaPage").then((m) => ({
    default: m.DashboardTurmaPage,
  }))
);
const MapaHabilidadesPage = lazy(() =>
  import("./pages/analytics/MapaHabilidadesPage").then((m) => ({
    default: m.MapaHabilidadesPage,
  }))
);
const RankingTurmasPage = lazy(() =>
  import("./pages/analytics/RankingTurmasPage").then((m) => ({
    default: m.RankingTurmasPage,
  }))
);
const RendimentoEscolarPage = lazy(() =>
  import("./pages/analytics/RendimentoEscolarPage").then((m) => ({
    default: m.RendimentoEscolarPage,
  }))
);

// Câmeras e Monitoração
const CamerasPage = lazy(() =>
  import("./pages/cameras/CamerasPage").then((m) => ({
    default: m.CamerasPage,
  }))
);
const MonitoracaoDashboardPage = lazy(() =>
  import("./pages/monitoracao/MonitoracaoDashboardPage").then((m) => ({
    default: m.MonitoracaoDashboardPage,
  }))
);
const MonitoracaoTempoRealPage = lazy(() =>
  import("./pages/monitoracao/MonitoracaoTempoRealPage").then((m) => ({
    default: m.MonitoracaoTempoRealPage,
  }))
);

// Menu admin pages
const MenusPage = lazy(() =>
  import("./pages/menus/MenusPage")
);
const MenuCatalogoPage = lazy(() =>
  import("./pages/menus/MenuCatalogoPage")
);
const MenuConfiguracaoPage = lazy(() =>
  import("./pages/menus/MenuConfiguracaoPage")
);

// Transporte Escolar
const TransporteDashboardPage = lazy(() =>
  import("./pages/transportes/TransporteDashboardPage").then((m) => ({
    default: m.TransporteDashboardPage,
  }))
);
const VeiculoListPage = lazy(() =>
  import("./pages/transportes/VeiculoListPage").then((m) => ({
    default: m.VeiculoListPage,
  }))
);
const VeiculoFormPage = lazy(() =>
  import("./pages/transportes/VeiculoFormPage").then((m) => ({
    default: m.VeiculoFormPage,
  }))
);
const VeiculoViewPage = lazy(() =>
  import("./pages/transportes/VeiculoViewPage").then((m) => ({
    default: m.VeiculoViewPage,
  }))
);
const MotoristaListPage = lazy(() =>
  import("./pages/transportes/MotoristaListPage").then((m) => ({
    default: m.MotoristaListPage,
  }))
);
const MotoristaFormPage = lazy(() =>
  import("./pages/transportes/MotoristaFormPage").then((m) => ({
    default: m.MotoristaFormPage,
  }))
);
const MonitorListPage = lazy(() =>
  import("./pages/transportes/MonitorListPage").then((m) => ({
    default: m.MonitorListPage,
  }))
);
const MonitorFormPage = lazy(() =>
  import("./pages/transportes/MonitorFormPage").then((m) => ({
    default: m.MonitorFormPage,
  }))
);
const ContratoListPage = lazy(() =>
  import("./pages/transportes/ContratoListPage").then((m) => ({
    default: m.ContratoListPage,
  }))
);
const ContratoFormPage = lazy(() =>
  import("./pages/transportes/ContratoFormPage").then((m) => ({
    default: m.ContratoFormPage,
  }))
);
const PontoEmbarqueListPage = lazy(() =>
  import("./pages/transportes/PontoEmbarqueListPage").then((m) => ({
    default: m.PontoEmbarqueListPage,
  }))
);
const PontoEmbarqueFormPage = lazy(() =>
  import("./pages/transportes/PontoEmbarqueFormPage").then((m) => ({
    default: m.PontoEmbarqueFormPage,
  }))
);
const RotaListPage = lazy(() =>
  import("./pages/transportes/RotaListPage").then((m) => ({
    default: m.RotaListPage,
  }))
);
const RotaFormPage = lazy(() =>
  import("./pages/transportes/RotaFormPage").then((m) => ({
    default: m.RotaFormPage,
  }))
);
const RotaViewPage = lazy(() =>
  import("./pages/transportes/RotaViewPage").then((m) => ({
    default: m.RotaViewPage,
  }))
);
const MotoristaViewPage = lazy(() =>
  import("./pages/transportes/MotoristaViewPage").then((m) => ({
    default: m.MotoristaViewPage,
  }))
);
const MonitorViewPage = lazy(() =>
  import("./pages/transportes/MonitorViewPage").then((m) => ({
    default: m.MonitorViewPage,
  }))
);
const ContratoViewPage = lazy(() =>
  import("./pages/transportes/ContratoViewPage").then((m) => ({
    default: m.ContratoViewPage,
  }))
);
const PontoEmbarqueViewPage = lazy(() =>
  import("./pages/transportes/PontoEmbarqueViewPage").then((m) => ({
    default: m.PontoEmbarqueViewPage,
  }))
);

// Alunos Transporte pages (Fase 3)
const AlunoTransporteListPage = lazy(() =>
  import("./pages/transportes/AlunoTransporteListPage").then((m) => ({
    default: m.AlunoTransporteListPage,
  }))
);
const AlunoTransporteFormPage = lazy(() =>
  import("./pages/transportes/AlunoTransporteFormPage").then((m) => ({
    default: m.AlunoTransporteFormPage,
  }))
);
const AlunoTransporteViewPage = lazy(() =>
  import("./pages/transportes/AlunoTransporteViewPage").then((m) => ({
    default: m.AlunoTransporteViewPage,
  }))
);

// Execução de Rotas pages (Fase 4)
const ExecucaoRotaListPage = lazy(() =>
  import("./pages/transportes/ExecucaoRotaListPage").then((m) => ({
    default: m.ExecucaoRotaListPage,
  }))
);
const ExecucaoRotaFormPage = lazy(() =>
  import("./pages/transportes/ExecucaoRotaFormPage").then((m) => ({
    default: m.ExecucaoRotaFormPage,
  }))
);
const ExecucaoRotaViewPage = lazy(() =>
  import("./pages/transportes/ExecucaoRotaViewPage").then((m) => ({
    default: m.ExecucaoRotaViewPage,
  }))
);

// Manutenção e Abastecimento pages
const ManutencaoListPage = lazy(() =>
  import("./pages/transportes/ManutencaoListPage").then((m) => ({
    default: m.ManutencaoListPage,
  }))
);
const ManutencaoFormPage = lazy(() =>
  import("./pages/transportes/ManutencaoFormPage").then((m) => ({
    default: m.ManutencaoFormPage,
  }))
);
const ManutencaoViewPage = lazy(() =>
  import("./pages/transportes/ManutencaoViewPage").then((m) => ({
    default: m.ManutencaoViewPage,
  }))
);
const AbastecimentoListPage = lazy(() =>
  import("./pages/transportes/AbastecimentoListPage").then((m) => ({
    default: m.AbastecimentoListPage,
  }))
);
const AbastecimentoFormPage = lazy(() =>
  import("./pages/transportes/AbastecimentoFormPage").then((m) => ({
    default: m.AbastecimentoFormPage,
  }))
);
const AbastecimentoViewPage = lazy(() =>
  import("./pages/transportes/AbastecimentoViewPage").then((m) => ({
    default: m.AbastecimentoViewPage,
  }))
);

// Ocorrências, Solicitações, Despesas, GPS, Configurações (Fase 6)
const OcorrenciaListPage = lazy(() =>
  import("./pages/transportes/OcorrenciaListPage").then((m) => ({
    default: m.OcorrenciaListPage,
  }))
);
const OcorrenciaFormPage = lazy(() =>
  import("./pages/transportes/OcorrenciaFormPage").then((m) => ({
    default: m.OcorrenciaFormPage,
  }))
);
const OcorrenciaViewPage = lazy(() =>
  import("./pages/transportes/OcorrenciaViewPage").then((m) => ({
    default: m.OcorrenciaViewPage,
  }))
);
const SolicitacaoListPage = lazy(() =>
  import("./pages/transportes/SolicitacaoListPage").then((m) => ({
    default: m.SolicitacaoListPage,
  }))
);
const SolicitacaoFormPage = lazy(() =>
  import("./pages/transportes/SolicitacaoFormPage").then((m) => ({
    default: m.SolicitacaoFormPage,
  }))
);
const SolicitacaoViewPage = lazy(() =>
  import("./pages/transportes/SolicitacaoViewPage").then((m) => ({
    default: m.SolicitacaoViewPage,
  }))
);
const DespesaListPage = lazy(() =>
  import("./pages/transportes/DespesaListPage").then((m) => ({
    default: m.DespesaListPage,
  }))
);
const DespesaFormPage = lazy(() =>
  import("./pages/transportes/DespesaFormPage").then((m) => ({
    default: m.DespesaFormPage,
  }))
);
const DespesaViewPage = lazy(() =>
  import("./pages/transportes/DespesaViewPage").then((m) => ({
    default: m.DespesaViewPage,
  }))
);
const RastreamentoGpsPage = lazy(() =>
  import("./pages/transportes/RastreamentoGpsPage").then((m) => ({
    default: m.RastreamentoGpsPage,
  }))
);
const ConfiguracaoTransportePage = lazy(() =>
  import("./pages/transportes/ConfiguracaoTransportePage").then((m) => ({
    default: m.ConfiguracaoTransportePage,
  }))
);

// =====================================================
// Recursos Humanos (RH) — Licitação Cascavel/CE
// =====================================================
const RHModulePage = lazy(() =>
  import("./pages/rh/RHModulePage").then((m) => ({
    default: m.RHModulePage,
  }))
);

const MeuPontoPage = lazy(() =>
  import("./pages/folha-ponto/MeuPontoPage").then((m) => ({
    default: m.MeuPontoPage,
  }))
);

// Educacenso (Censo Escolar INEP/MEC)
const EducacensoIndexPage = lazy(() =>
  import("./pages/educacenso/EducacensoIndexPage").then((m) => ({
    default: m.EducacensoIndexPage,
  }))
);
const EducacensoJobDetailPage = lazy(() =>
  import("./pages/educacenso/EducacensoJobDetailPage").then((m) => ({
    default: m.EducacensoJobDetailPage,
  }))
);
const EducacensoGerarPage = lazy(() =>
  import("./pages/educacenso/EducacensoGerarPage").then((m) => ({
    default: m.EducacensoGerarPage,
  }))
);
const EducacensoImportarPage = lazy(() =>
  import("./pages/educacenso/EducacensoImportarPage").then((m) => ({
    default: m.EducacensoImportarPage,
  }))
);
const EducacensoValidarPage = lazy(() =>
  import("./pages/educacenso/EducacensoValidarPage").then((m) => ({
    default: m.EducacensoValidarPage,
  }))
);

// Formação de Servidores
const FormacaoServidoresListPage = lazy(() =>
  import("./pages/formacao/servidores/FormacaoServidoresListPage").then((m) => ({
    default: m.FormacaoServidoresListPage,
  }))
);
const FormacaoServidoresFormPage = lazy(() =>
  import("./pages/formacao/servidores/FormacaoServidoresFormPage").then((m) => ({
    default: m.FormacaoServidoresFormPage,
  }))
);
const FormacaoServidoresViewPage = lazy(() =>
  import("./pages/formacao/servidores/FormacaoServidoresViewPage").then((m) => ({
    default: m.FormacaoServidoresViewPage,
  }))
);
const FormacaoMinhasInscricoesPage = lazy(() =>
  import("./pages/formacao/FormacaoMinhasInscricoesPage").then((m) => ({
    default: m.FormacaoMinhasInscricoesPage,
  }))
);
const FormacaoServidorDetalhePublicoPage = lazy(() =>
  import("./pages/formacao/FormacaoServidorDetalhePublicoPage").then((m) => ({
    default: m.FormacaoServidorDetalhePublicoPage,
  }))
);

// Módulo Formador
const FormadorModulePage = lazy(() =>
  import("./pages/formacao/FormadorModulePage").then((m) => ({
    default: m.FormadorModulePage,
  }))
);
const GuiaPlanejamentoBuilderPage = lazy(() =>
  import("./pages/formacao/guias/GuiaPlanejamentoBuilderPage").then((m) => ({
    default: m.GuiaPlanejamentoBuilderPage,
  }))
);
const FormadorDetailPage = lazy(() =>
  import("./pages/formacao/formadores/FormadorDetailPage").then((m) => ({
    default: m.FormadorDetailPage,
  }))
);
const FormacaoContinuadaDetailPage = lazy(() =>
  import("./pages/formacao/continuada/FormacaoContinuadaDetailPage").then((m) => ({
    default: m.FormacaoContinuadaDetailPage,
  }))
);

// Gestão de Processos
const ProcessoListPage = lazy(() =>
  import("./pages/gestao-processos/ProcessoListPage").then((m) => ({
    default: m.ProcessoListPage,
  }))
);
const ProcessoFormPage = lazy(() =>
  import("./pages/gestao-processos/ProcessoFormPage").then((m) => ({
    default: m.ProcessoFormPage,
  }))
);
const ProcessoViewPage = lazy(() =>
  import("./pages/gestao-processos/ProcessoViewPage").then((m) => ({
    default: m.ProcessoViewPage,
  }))
);

// Conselho Escolar
const ConselhoEscolarListPage = lazy(() =>
  import("./pages/conselho-escolar/ConselhoEscolarListPage").then((m) => ({
    default: m.ConselhoEscolarListPage,
  }))
);
const ConselhoEscolarDetailPage = lazy(() =>
  import("./pages/conselho-escolar/ConselhoEscolarDetailPage").then((m) => ({
    default: m.ConselhoEscolarDetailPage,
  }))
);

// Diário AEE
const DiarioAEEListPage = lazy(() =>
  import("./pages/diario-aee/DiarioAEEListPage").then((m) => ({
    default: m.DiarioAEEListPage,
  }))
);
const DiarioAEEDetailPage = lazy(() =>
  import("./pages/diario-aee/DiarioAEEDetailPage").then((m) => ({
    default: m.DiarioAEEDetailPage,
  }))
);

// Financeiro
const FinanceiroDashboardPage = lazy(() =>
  import("./pages/financeiro/FinanceiroDashboardPage").then((m) => ({
    default: m.FinanceiroDashboardPage,
  }))
);
const PlanosAplicacaoPage = lazy(() =>
  import("./pages/financeiro/PlanosAplicacaoPage").then((m) => ({
    default: m.PlanosAplicacaoPage,
  }))
);
const PlanoAplicacaoDetailPage = lazy(() =>
  import("./pages/financeiro/PlanoAplicacaoDetailPage").then((m) => ({
    default: m.PlanoAplicacaoDetailPage,
  }))
);
const EmpenhosPage = lazy(() =>
  import("./pages/financeiro/EmpenhosPage").then((m) => ({
    default: m.EmpenhosPage,
  }))
);
const EmpenhoDetailPage = lazy(() =>
  import("./pages/financeiro/EmpenhoDetailPage").then((m) => ({
    default: m.EmpenhoDetailPage,
  }))
);
const PrestacaoContasDetailPage = lazy(() =>
  import("./pages/financeiro/PrestacaoContasDetailPage").then((m) => ({
    default: m.PrestacaoContasDetailPage,
  }))
);

// Patrimônio
const PatrimonioDashboardPage = lazy(() =>
  import("./pages/patrimonio/PatrimonioDashboardPage").then((m) => ({
    default: m.PatrimonioDashboardPage,
  }))
);
const BemPatrimonialDetailPage = lazy(() =>
  import("./pages/patrimonio/BemPatrimonialDetailPage").then((m) => ({
    default: m.BemPatrimonialDetailPage,
  }))
);
const InventarioDetailPage = lazy(() =>
  import("./pages/patrimonio/InventarioDetailPage").then((m) => ({
    default: m.InventarioDetailPage,
  }))
);
const RequisicaoMaterialDetailPage = lazy(() =>
  import("./pages/patrimonio/RequisicaoMaterialDetailPage").then((m) => ({
    default: m.RequisicaoMaterialDetailPage,
  }))
);

// Fardamento (Uniformes)
const FardamentoListPage = lazy(() =>
  import("./pages/fardamento/FardamentoListPage").then((m) => ({
    default: m.FardamentoListPage,
  }))
);
const FardamentoFormPage = lazy(() =>
  import("./pages/fardamento/FardamentoFormPage").then((m) => ({
    default: m.FardamentoFormPage,
  }))
);
const FardamentoViewPage = lazy(() =>
  import("./pages/fardamento/FardamentoViewPage").then((m) => ({
    default: m.FardamentoViewPage,
  }))
);

// Biblioteca Escolar
const BibliotecaDashboardPage = lazy(() =>
  import("./pages/biblioteca/BibliotecaDashboardPage").then((m) => ({
    default: m.BibliotecaDashboardPage,
  }))
);
const BibliotecaConfigPage = lazy(() =>
  import("./pages/biblioteca/BibliotecaConfigPage").then((m) => ({
    default: m.BibliotecaConfigPage,
  }))
);
const ObrasListPage = lazy(() =>
  import("./pages/biblioteca/ObrasListPage").then((m) => ({
    default: m.ObrasListPage,
  }))
);
const ObraFormPage = lazy(() =>
  import("./pages/biblioteca/ObraFormPage").then((m) => ({
    default: m.ObraFormPage,
  }))
);
const ObraViewPage = lazy(() =>
  import("./pages/biblioteca/ObraViewPage").then((m) => ({
    default: m.ObraViewPage,
  }))
);
const AutoresListPage = lazy(() =>
  import("./pages/biblioteca/AutoresListPage").then((m) => ({
    default: m.AutoresListPage,
  }))
);
const AutorFormPage = lazy(() =>
  import("./pages/biblioteca/AutorFormPage").then((m) => ({
    default: m.AutorFormPage,
  }))
);
const EditorasListPage = lazy(() =>
  import("./pages/biblioteca/EditorasListPage").then((m) => ({
    default: m.EditorasListPage,
  }))
);
const EditoraFormPage = lazy(() =>
  import("./pages/biblioteca/EditoraFormPage").then((m) => ({
    default: m.EditoraFormPage,
  }))
);
const CategoriasListPage = lazy(() =>
  import("./pages/biblioteca/CategoriasListPage").then((m) => ({
    default: m.CategoriasListPage,
  }))
);
const CategoriaFormPage = lazy(() =>
  import("./pages/biblioteca/CategoriaFormPage").then((m) => ({
    default: m.CategoriaFormPage,
  }))
);
const EtiquetasListPage = lazy(() =>
  import("./pages/biblioteca/EtiquetasListPage").then((m) => ({
    default: m.EtiquetasListPage,
  }))
);
const EtiquetaFormPage = lazy(() =>
  import("./pages/biblioteca/EtiquetaFormPage").then((m) => ({
    default: m.EtiquetaFormPage,
  }))
);
const ExemplaresListPage = lazy(() =>
  import("./pages/biblioteca/ExemplaresListPage").then((m) => ({
    default: m.ExemplaresListPage,
  }))
);
const ExemplarFormPage = lazy(() =>
  import("./pages/biblioteca/ExemplarFormPage").then((m) => ({
    default: m.ExemplarFormPage,
  }))
);
// Biblioteca - Circulação (Fase 2)
const EmprestimosListPage = lazy(() =>
  import("./pages/biblioteca/EmprestimosListPage").then((m) => ({
    default: m.EmprestimosListPage,
  }))
);
const EmprestimoFormPage = lazy(() =>
  import("./pages/biblioteca/EmprestimoFormPage").then((m) => ({
    default: m.EmprestimoFormPage,
  }))
);
const EmprestimoViewPage = lazy(() =>
  import("./pages/biblioteca/EmprestimoViewPage").then((m) => ({
    default: m.EmprestimoViewPage,
  }))
);
const ReservasListPage = lazy(() =>
  import("./pages/biblioteca/ReservasListPage").then((m) => ({
    default: m.ReservasListPage,
  }))
);
const ReservaFormPage = lazy(() =>
  import("./pages/biblioteca/ReservaFormPage").then((m) => ({
    default: m.ReservaFormPage,
  }))
);
const UsuariosListPage = lazy(() =>
  import("./pages/biblioteca/UsuariosListPage").then((m) => ({
    default: m.UsuariosListPage,
  }))
);
const UsuarioFormPage = lazy(() =>
  import("./pages/biblioteca/UsuarioFormPage").then((m) => ({
    default: m.UsuarioFormPage,
  }))
);

// Biblioteca - Financeiro (Fase 3)
const MultasListPage = lazy(() =>
  import("./pages/biblioteca/MultasListPage").then((m) => ({
    default: m.MultasListPage,
  }))
);
const MultaViewPage = lazy(() =>
  import("./pages/biblioteca/MultaViewPage").then((m) => ({
    default: m.MultaViewPage,
  }))
);
// Biblioteca - Patrimônio (Fase 4)
const InventariosListPage = lazy(() =>
  import("./pages/biblioteca/InventariosListPage").then((m) => ({
    default: m.InventariosListPage,
  }))
);
const InventarioFormPage = lazy(() =>
  import("./pages/biblioteca/InventarioFormPage").then((m) => ({
    default: m.InventarioFormPage,
  }))
);
const InventarioViewPage = lazy(() =>
  import("./pages/biblioteca/InventarioViewPage").then((m) => ({
    default: m.InventarioViewPage,
  }))
);
const AquisicoesListPage = lazy(() =>
  import("./pages/biblioteca/AquisicoesListPage").then((m) => ({
    default: m.AquisicoesListPage,
  }))
);
const AquisicaoFormPage = lazy(() =>
  import("./pages/biblioteca/AquisicaoFormPage").then((m) => ({
    default: m.AquisicaoFormPage,
  }))
);
const DoacoesListPage = lazy(() =>
  import("./pages/biblioteca/DoacoesListPage").then((m) => ({
    default: m.DoacoesListPage,
  }))
);
const DoacaoFormPage = lazy(() =>
  import("./pages/biblioteca/DoacaoFormPage").then((m) => ({
    default: m.DoacaoFormPage,
  }))
);
const BaixasListPage = lazy(() =>
  import("./pages/biblioteca/BaixasListPage").then((m) => ({
    default: m.BaixasListPage,
  }))
);
const BaixaFormPage = lazy(() =>
  import("./pages/biblioteca/BaixaFormPage").then((m) => ({
    default: m.BaixaFormPage,
  }))
);
const MovimentacoesListPage = lazy(() =>
  import("./pages/biblioteca/MovimentacoesListPage").then((m) => ({
    default: m.MovimentacoesListPage,
  }))
);

// Merenda Escolar
const MerendaIndexPage = lazy(() =>
  import("./pages/merenda/MerendaIndexPage").then((m) => ({
    default: m.MerendaIndexPage,
  }))
);
const CozinhasListPage = lazy(() =>
  import("./pages/merenda/CozinhasListPage").then((m) => ({
    default: m.CozinhasListPage,
  }))
);
const CozinhaFormPage = lazy(() =>
  import("./pages/merenda/CozinhaFormPage").then((m) => ({
    default: m.CozinhaFormPage,
  }))
);
const GruposAlimentaresListPage = lazy(() =>
  import("./pages/merenda/GruposAlimentaresListPage").then((m) => ({
    default: m.GruposAlimentaresListPage,
  }))
);
const GrupoAlimentarFormPage = lazy(() =>
  import("./pages/merenda/GrupoAlimentarFormPage").then((m) => ({
    default: m.GrupoAlimentarFormPage,
  }))
);
const UnidadesMedidaListPage = lazy(() =>
  import("./pages/merenda/UnidadesMedidaListPage").then((m) => ({
    default: m.UnidadesMedidaListPage,
  }))
);
const UnidadeMedidaFormPage = lazy(() =>
  import("./pages/merenda/UnidadeMedidaFormPage").then((m) => ({
    default: m.UnidadeMedidaFormPage,
  }))
);
const AlimentosListPage = lazy(() =>
  import("./pages/merenda/AlimentosListPage").then((m) => ({
    default: m.AlimentosListPage,
  }))
);
const AlimentoFormPage = lazy(() =>
  import("./pages/merenda/AlimentoFormPage").then((m) => ({
    default: m.AlimentoFormPage,
  }))
);
const TiposRefeicaoListPage = lazy(() =>
  import("./pages/merenda/TiposRefeicaoListPage").then((m) => ({
    default: m.TiposRefeicaoListPage,
  }))
);
const TipoRefeicaoFormPage = lazy(() =>
  import("./pages/merenda/TipoRefeicaoFormPage").then((m) => ({
    default: m.TipoRefeicaoFormPage,
  }))
);
const ReceitasListPage = lazy(() =>
  import("./pages/merenda/ReceitasListPage").then((m) => ({
    default: m.ReceitasListPage,
  }))
);
const ReceitaFormPage = lazy(() =>
  import("./pages/merenda/ReceitaFormPage").then((m) => ({
    default: m.ReceitaFormPage,
  }))
);
const CardapiosListPage = lazy(() =>
  import("./pages/merenda/CardapiosListPage").then((m) => ({
    default: m.CardapiosListPage,
  }))
);
const CardapioFormPage = lazy(() =>
  import("./pages/merenda/CardapioFormPage").then((m) => ({
    default: m.CardapioFormPage,
  }))
);
const EstoquesListPage = lazy(() =>
  import("./pages/merenda/EstoquesListPage").then((m) => ({
    default: m.EstoquesListPage,
  }))
);
const EstoqueFormPage = lazy(() =>
  import("./pages/merenda/EstoqueFormPage").then((m) => ({
    default: m.EstoqueFormPage,
  }))
);
const EstoqueDetailPage = lazy(() =>
  import("./pages/merenda/EstoqueDetailPage").then((m) => ({
    default: m.EstoqueDetailPage,
  }))
);
const FornecedoresListPage = lazy(() =>
  import("./pages/merenda/FornecedoresListPage").then((m) => ({
    default: m.FornecedoresListPage,
  }))
);
const FornecedorFormPage = lazy(() =>
  import("./pages/merenda/FornecedorFormPage").then((m) => ({
    default: m.FornecedorFormPage,
  }))
);
const MerendaContratosListPage = lazy(() =>
  import("./pages/merenda/ContratosListPage").then((m) => ({
    default: m.ContratosListPage,
  }))
);
const MerendaContratoFormPage = lazy(() =>
  import("./pages/merenda/ContratoFormPage").then((m) => ({
    default: m.ContratoFormPage,
  }))
);
const PedidosCompraListPage = lazy(() =>
  import("./pages/merenda/PedidosCompraListPage").then((m) => ({
    default: m.PedidosCompraListPage,
  }))
);
const PedidoCompraFormPage = lazy(() =>
  import("./pages/merenda/PedidoCompraFormPage").then((m) => ({
    default: m.PedidoCompraFormPage,
  }))
);
const EntregasListPage = lazy(() =>
  import("./pages/merenda/EntregasListPage").then((m) => ({
    default: m.EntregasListPage,
  }))
);
const EntregaFormPage = lazy(() =>
  import("./pages/merenda/EntregaFormPage").then((m) => ({
    default: m.EntregaFormPage,
  }))
);

// Merenda - Fase 6: Detail Pages
const PedidoCompraDetailPage = lazy(() =>
  import("./pages/merenda/PedidoCompraDetailPage").then((m) => ({
    default: m.PedidoCompraDetailPage,
  }))
);
const EntregaDetailPage = lazy(() =>
  import("./pages/merenda/EntregaDetailPage").then((m) => ({
    default: m.EntregaDetailPage,
  }))
);

// Merenda - Fase 7: Execucao Diaria
const RefeicoesServidasListPage = lazy(() =>
  import("./pages/merenda/RefeicoesServidasListPage").then((m) => ({
    default: m.RefeicoesServidasListPage,
  }))
);
const RefeicaoServidaFormPage = lazy(() =>
  import("./pages/merenda/RefeicaoServidaFormPage").then((m) => ({
    default: m.RefeicaoServidaFormPage,
  }))
);
const RefeicaoServidaDetailPage = lazy(() =>
  import("./pages/merenda/RefeicaoServidaDetailPage").then((m) => ({
    default: m.RefeicaoServidaDetailPage,
  }))
);
const ProducoesDiariasListPage = lazy(() =>
  import("./pages/merenda/ProducoesDiariasListPage").then((m) => ({
    default: m.ProducoesDiariasListPage,
  }))
);
const ProducaoDiariaFormPage = lazy(() =>
  import("./pages/merenda/ProducaoDiariaFormPage").then((m) => ({
    default: m.ProducaoDiariaFormPage,
  }))
);
const ProducaoDiariaDetailPage = lazy(() =>
  import("./pages/merenda/ProducaoDiariaDetailPage").then((m) => ({
    default: m.ProducaoDiariaDetailPage,
  }))
);

// Merenda - Fase 8: Escalas de Trabalho
const EscalasTrabalhoListPage = lazy(() =>
  import("./pages/merenda/EscalasTrabalhoListPage").then((m) => ({
    default: m.EscalasTrabalhoListPage,
  }))
);
const EscalaTrabalhoFormPage = lazy(() =>
  import("./pages/merenda/EscalaTrabalhoFormPage").then((m) => ({
    default: m.EscalaTrabalhoFormPage,
  }))
);

// Merenda - Fase 9: Dietas Especiais
const DietasEspeciaisListPage = lazy(() =>
  import("./pages/merenda/DietasEspeciaisListPage").then((m) => ({
    default: m.DietasEspeciaisListPage,
  }))
);
const DietaEspecialFormPage = lazy(() =>
  import("./pages/merenda/DietaEspecialFormPage").then((m) => ({
    default: m.DietaEspecialFormPage,
  }))
);
const AlunosDietasListPage = lazy(() =>
  import("./pages/merenda/AlunosDietasListPage").then((m) => ({
    default: m.AlunosDietasListPage,
  }))
);
const AlunoDietaFormPage = lazy(() =>
  import("./pages/merenda/AlunoDietaFormPage").then((m) => ({
    default: m.AlunoDietaFormPage,
  }))
);
const AlunoDietaDetailPage = lazy(() =>
  import("./pages/merenda/AlunoDietaDetailPage").then((m) => ({
    default: m.AlunoDietaDetailPage,
  }))
);

// Merenda - Fase 10: Equipamentos
const EquipamentosMerendaListPage = lazy(() =>
  import("./pages/merenda/EquipamentosMerendaListPage").then((m) => ({
    default: m.EquipamentosMerendaListPage,
  }))
);
const EquipamentoMerendaFormPage = lazy(() =>
  import("./pages/merenda/EquipamentoMerendaFormPage").then((m) => ({
    default: m.EquipamentoMerendaFormPage,
  }))
);
const EquipamentoMerendaDetailPage = lazy(() =>
  import("./pages/merenda/EquipamentoMerendaDetailPage").then((m) => ({
    default: m.EquipamentoMerendaDetailPage,
  }))
);

// Merenda - Fase 11: Financeiro
const DespesasMerendaListPage = lazy(() =>
  import("./pages/merenda/DespesasMerendaListPage").then((m) => ({
    default: m.DespesasMerendaListPage,
  }))
);
const DespesaMerendaFormPage = lazy(() =>
  import("./pages/merenda/DespesaMerendaFormPage").then((m) => ({
    default: m.DespesaMerendaFormPage,
  }))
);
const OrcamentosMerendaListPage = lazy(() =>
  import("./pages/merenda/OrcamentosMerendaListPage").then((m) => ({
    default: m.OrcamentosMerendaListPage,
  }))
);
const OrcamentoMerendaFormPage = lazy(() =>
  import("./pages/merenda/OrcamentoMerendaFormPage").then((m) => ({
    default: m.OrcamentoMerendaFormPage,
  }))
);
const DespesaMerendaDetailPage = lazy(() =>
  import("./pages/merenda/DespesaMerendaDetailPage").then((m) => ({
    default: m.DespesaMerendaDetailPage,
  }))
);
const OrcamentoMerendaDetailPage = lazy(() =>
  import("./pages/merenda/OrcamentoMerendaDetailPage").then((m) => ({
    default: m.OrcamentoMerendaDetailPage,
  }))
);
const AvaliacoesAceitabilidadeListPage = lazy(() =>
  import("./pages/merenda/AvaliacoesAceitabilidadeListPage").then((m) => ({
    default: m.AvaliacoesAceitabilidadeListPage,
  }))
);
const AvaliacaoAceitabilidadeFormPage = lazy(() =>
  import("./pages/merenda/AvaliacaoAceitabilidadeFormPage").then((m) => ({
    default: m.AvaliacaoAceitabilidadeFormPage,
  }))
);
const AvaliacaoAceitabilidadeDetailPage = lazy(() =>
  import("./pages/merenda/AvaliacaoAceitabilidadeDetailPage").then((m) => ({
    default: m.AvaliacaoAceitabilidadeDetailPage,
  }))
);
const ConfiguracoesMerendaListPage = lazy(() =>
  import("./pages/merenda/ConfiguracoesMerendaListPage").then((m) => ({
    default: m.ConfiguracoesMerendaListPage,
  }))
);
const ConfiguracaoMerendaFormPage = lazy(() =>
  import("./pages/merenda/ConfiguracaoMerendaFormPage").then((m) => ({
    default: m.ConfiguracaoMerendaFormPage,
  }))
);

// Fase 13 - Analytics e Dashboards
const MerendaDashboardPage = lazy(() =>
  import("./pages/merenda/MerendaDashboardPage").then((m) => ({
    default: m.MerendaDashboardPage,
  }))
);
const EstoqueReportPage = lazy(() =>
  import("./pages/merenda/analytics/EstoqueReportPage").then((m) => ({
    default: m.EstoqueReportPage,
  }))
);
const VencimentoReportPage = lazy(() =>
  import("./pages/merenda/analytics/VencimentoReportPage").then((m) => ({
    default: m.VencimentoReportPage,
  }))
);
const RefeicoesReportPage = lazy(() =>
  import("./pages/merenda/analytics/RefeicoesReportPage").then((m) => ({
    default: m.RefeicoesReportPage,
  }))
);
const CustosReportPage = lazy(() =>
  import("./pages/merenda/analytics/CustosReportPage").then((m) => ({
    default: m.CustosReportPage,
  }))
);
const PNAEReportPage = lazy(() =>
  import("./pages/merenda/analytics/PNAEReportPage").then((m) => ({
    default: m.PNAEReportPage,
  }))
);
const AceitabilidadeReportPage = lazy(() =>
  import("./pages/merenda/analytics/AceitabilidadeReportPage").then((m) => ({
    default: m.AceitabilidadeReportPage,
  }))
);
const DesperdicioReportPage = lazy(() =>
  import("./pages/merenda/analytics/DesperdicioReportPage").then((m) => ({
    default: m.DesperdicioReportPage,
  }))
);
const CardapiosReportPage = lazy(() =>
  import("./pages/merenda/analytics/CardapiosReportPage").then((m) => ({
    default: m.CardapiosReportPage,
  }))
);

// Responsável pages - Placeholder imports (serão criados na Fase 4)
const ResponsavelDashboardPage = lazy(() =>
  import("./pages/responsavel/ResponsavelDashboardPage").then((m) => ({
    default: m.ResponsavelDashboardPage,
  }))
);
const ResponsavelAlunoDetailPage = lazy(() =>
  import("./pages/responsavel/ResponsavelAlunoDetailPage").then((m) => ({
    default: m.ResponsavelAlunoDetailPage,
  }))
);
const ResponsavelFrequenciaFacialPage = lazy(() =>
  import("./pages/responsavel/ResponsavelFrequenciaFacialPage").then((m) => ({
    default: m.ResponsavelFrequenciaFacialPage,
  }))
);
const ResponsavelPerfilPage = lazy(() =>
  import("./pages/responsavel/ResponsavelPerfilPage").then((m) => ({
    default: m.ResponsavelPerfilPage,
  }))
);

// Pre-Matricula - Responsavel
const PreMatriculaListPage = lazy(() =>
  import("./pages/responsavel/pre-matricula/PreMatriculaListPage").then((m) => ({
    default: m.PreMatriculaListPage,
  }))
);
const PreMatriculaWizardPage = lazy(() =>
  import("./pages/responsavel/pre-matricula/PreMatriculaWizardPage").then((m) => ({
    default: m.PreMatriculaWizardPage,
  }))
);
const PreMatriculaDetailPage = lazy(() =>
  import("./pages/responsavel/pre-matricula/PreMatriculaDetailPage").then((m) => ({
    default: m.PreMatriculaDetailPage,
  }))
);
const PreMatriculaSuccessPage = lazy(() =>
  import("./pages/responsavel/pre-matricula/PreMatriculaSuccessPage").then((m) => ({
    default: m.PreMatriculaSuccessPage,
  }))
);
// Aluno pages
const AlunoDashboardPage = lazy(() =>
  import("./pages/aluno/AlunoDashboardPage").then((m) => ({
    default: m.AlunoDashboardPage,
  }))
);
const AlunoNotasPage = lazy(() =>
  import("./pages/aluno/AlunoNotasPage").then((m) => ({
    default: m.AlunoNotasPage,
  }))
);
const AlunoFrequenciaPage = lazy(() =>
  import("./pages/aluno/AlunoFrequenciaPage").then((m) => ({
    default: m.AlunoFrequenciaPage,
  }))
);
const AlunoHorarioPage = lazy(() =>
  import("./pages/aluno/AlunoHorarioPage").then((m) => ({
    default: m.AlunoHorarioPage,
  }))
);
const AlunoDiarioClassePage = lazy(() =>
  import("./pages/aluno/AlunoDiarioClassePage").then((m) => ({
    default: m.AlunoDiarioClassePage,
  }))
);
const AlunoAgendaPage = lazy(() =>
  import("./pages/aluno/AlunoAgendaPage").then((m) => ({
    default: m.AlunoAgendaPage,
  }))
);
const AlunoAudienciaLeituraPage = lazy(() =>
  import("./pages/aluno/AlunoAudienciaLeituraPage").then((m) => ({
    default: m.AlunoAudienciaLeituraPage,
  }))
);
const AlunoDocumentosPage = lazy(() =>
  import("./pages/aluno/AlunoDocumentosPage").then((m) => ({
    default: m.AlunoDocumentosPage,
  }))
);
const AlunoPerfilPage = lazy(() =>
  import("./pages/aluno/AlunoPerfilPage").then((m) => ({
    default: m.AlunoPerfilPage,
  }))
);
const AlunoAvaliacoesPage = lazy(() =>
  import("./pages/aluno/AlunoAvaliacoesPage").then((m) => ({
    default: m.AlunoAvaliacoesPage,
  }))
);
const AlunoTransportePage = lazy(() =>
  import("./pages/aluno/AlunoTransportePage").then((m) => ({
    default: m.AlunoTransportePage,
  }))
);
const AlunoBibliotecaPage = lazy(() =>
  import("./pages/aluno/AlunoBibliotecaPage").then((m) => ({
    default: m.AlunoBibliotecaPage,
  }))
);
const AlunoCarteiraEstudantilPage = lazy(() =>
  import("./pages/aluno/AlunoCarteiraEstudantilPage").then((m) => ({
    default: m.AlunoCarteiraEstudantilPage,
  }))
);

// Benefícios Sociais
const BeneficiosSociaisPage = lazy(() =>
  import("./pages/beneficios-sociais/BeneficiosSociaisPage").then((m) => ({
    default: m.BeneficiosSociaisPage,
  }))
);
const BeneficioAlunosPage = lazy(() =>
  import("./pages/beneficios-sociais/BeneficioAlunosPage").then((m) => ({
    default: m.BeneficioAlunosPage,
  }))
);

// AVA - Ambiente Virtual de Aprendizagem
const AvaListPage = lazy(() =>
  import("./components/ava/pages/AvaListPage").then((m) => ({
    default: m.AvaListPage,
  }))
);
const AvaPage = lazy(() =>
  import("./components/ava/pages/AvaPage").then((m) => ({
    default: m.AvaPage,
  }))
);
const AvaAlunoPage = lazy(() =>
  import("./components/ava/pages/AvaAlunoPage").then((m) => ({
    default: m.AvaAlunoPage,
  }))
);

// Auditoria
const AuditLogPage = lazy(() =>
  import("./pages/auditoria/AuditLogPage").then((m) => ({
    default: m.AuditLogPage,
  }))
);

// Legal pages
const PoliticaPrivacidadePage = lazy(() =>
  import("./pages/legal/PoliticaPrivacidadePageNew").then((m) => ({
    default: m.PoliticaPrivacidadePageNew,
  }))
);

// Gestão de Obras
const ObrasIndexPage = lazy(() =>
  import("./pages/obras/ObrasIndexPage").then((m) => ({
    default: m.ObrasIndexPage,
  }))
);
// Gestão de Obras - Cadastros Base
const TiposObraListPage = lazy(() =>
  import("./pages/obras/cadastros/TiposObraListPage").then((m) => ({
    default: m.TiposObraListPage,
  }))
);
const TipoObraFormPage = lazy(() =>
  import("./pages/obras/cadastros/TipoObraFormPage").then((m) => ({
    default: m.TipoObraFormPage,
  }))
);
const FiscaisListPage = lazy(() =>
  import("./pages/obras/cadastros/FiscaisListPage").then((m) => ({
    default: m.FiscaisListPage,
  }))
);
const FiscalFormPage = lazy(() =>
  import("./pages/obras/cadastros/FiscalFormPage").then((m) => ({
    default: m.FiscalFormPage,
  }))
);
const EmpreiteirasListPage = lazy(() =>
  import("./pages/obras/cadastros/EmpreiteirasListPage").then((m) => ({
    default: m.EmpreiteirasListPage,
  }))
);
const EmpreiteiraFormPage = lazy(() =>
  import("./pages/obras/cadastros/EmpreiteiraFormPage").then((m) => ({
    default: m.EmpreiteiraFormPage,
  }))
);
const EmpreiteiraViewPage = lazy(() =>
  import("./pages/obras/cadastros/EmpreiteiraViewPage").then((m) => ({
    default: m.EmpreiteiraViewPage,
  }))
);
const MateriaisObraListPage = lazy(() =>
  import("./pages/obras/cadastros/MateriaisListPage").then((m) => ({
    default: m.MateriaisListPage,
  }))
);
const MaterialObraFormPage = lazy(() =>
  import("./pages/obras/cadastros/MaterialFormPage").then((m) => ({
    default: m.MaterialFormPage,
  }))
);
const FornecedoresObraListPage = lazy(() =>
  import("./pages/obras/cadastros/FornecedoresListPage").then((m) => ({
    default: m.FornecedoresListPage,
  }))
);
const FornecedorObraFormPage = lazy(() =>
  import("./pages/obras/cadastros/FornecedorFormPage").then((m) => ({
    default: m.FornecedorFormPage,
  }))
);
const ConfiguracoesObraPage = lazy(() =>
  import("./pages/obras/cadastros/ConfiguracoesObraPage").then((m) => ({
    default: m.ConfiguracoesObraPage,
  }))
);
// Gestão de Obras - Obra Principal
const ObrasConstrucaoListPage = lazy(() =>
  import("./pages/obras/ObrasListPage").then((m) => ({
    default: m.ObrasListPage,
  }))
);
const ObraConstrucaoFormPage = lazy(() =>
  import("./pages/obras/ObraFormPage").then((m) => ({
    default: m.ObraFormPage,
  }))
);
const ObraConstrucaoViewPage = lazy(() =>
  import("./pages/obras/ObraViewPage").then((m) => ({
    default: m.ObraViewPage,
  }))
);
const ObraResumoTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraResumoTab").then((m) => ({
    default: m.ObraResumoTab,
  }))
);
const ObraProjetosTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraProjetosTab").then((m) => ({
    default: m.ObraProjetosTab,
  }))
);
const ObraCronogramaTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraCronogramaTab").then((m) => ({
    default: m.ObraCronogramaTab,
  }))
);
const ObraOrcamentoTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraOrcamentoTab").then((m) => ({
    default: m.ObraOrcamentoTab,
  }))
);

const ObraContratosTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraContratosTab").then((m) => ({
    default: m.ObraContratosTab,
  }))
);

const ObraMedicoesTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraMedicoesTab").then((m) => ({
    default: m.ObraMedicoesTab,
  }))
);

const ObraFinanceiroTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraFinanceiroTab").then((m) => ({
    default: m.ObraFinanceiroTab,
  }))
);
const ObraExecucaoTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraExecucaoTab").then((m) => ({
    default: m.ObraExecucaoTab,
  }))
);
const ObraDiarioTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraDiarioTab").then((m) => ({
    default: m.ObraDiarioTab,
  }))
);
const ObraFotosTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraFotosTab").then((m) => ({
    default: m.ObraFotosTab,
  }))
);
const ObraEstoqueTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraEstoqueTab").then((m) => ({
    default: m.ObraEstoqueTab,
  }))
);
const ObraEquipamentosTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraEquipamentosTab").then((m) => ({
    default: m.ObraEquipamentosTab,
  }))
);
const ObraFuncionariosTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraFuncionariosTab").then((m) => ({
    default: m.ObraFuncionariosTab,
  }))
);
const ObraVistoriasTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraVistoriasTab").then((m) => ({
    default: m.ObraVistoriasTab,
  }))
);
const ObraNaoConformidadesTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraNaoConformidadesTab").then((m) => ({
    default: m.ObraNaoConformidadesTab,
  }))
);
const ObraAcidentesTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraAcidentesTab").then((m) => ({
    default: m.ObraAcidentesTab,
  }))
);
const ObraDocumentosTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraDocumentosTab").then((m) => ({
    default: m.ObraDocumentosTab,
  }))
);
const ObraRelatoriosTab = lazy(() =>
  import("./pages/obras/obra-detail/ObraRelatoriosTab").then((m) => ({
    default: m.ObraRelatoriosTab,
  }))
);
const ObrasDashboardPage = lazy(() =>
  import("./pages/obras/ObrasDashboardPage").then((m) => ({
    default: m.ObrasDashboardPage,
  }))
);
const MedicoesPendentesPage = lazy(() =>
  import("./pages/obras/alertas/MedicoesPendentesPage").then((m) => ({
    default: m.MedicoesPendentesPage,
  }))
);
const NaoConformidadesAbertasPage = lazy(() =>
  import("./pages/obras/alertas/NaoConformidadesAbertasPage").then((m) => ({
    default: m.NaoConformidadesAbertasPage,
  }))
);
const DocumentosVencendoPage = lazy(() =>
  import("./pages/obras/alertas/DocumentosVencendoPage").then((m) => ({
    default: m.DocumentosVencendoPage,
  }))
);
const NotFoundPage = lazy(() =>
  import("./pages/not-found/NotFoundPage").then((m) => ({
    default: m.NotFoundPage,
  }))
);

// Admin - Tenants (super-admin only)
const TenantsListPage = lazy(() =>
  import("./pages/admin/tenants/TenantsListPage").then((m) => ({
    default: m.TenantsListPage,
  }))
);
const CreateTenantWizardPage = lazy(() =>
  import("./pages/admin/tenants/CreateTenantWizardPage").then((m) => ({
    default: m.CreateTenantWizardPage,
  }))
);
const TenantDetailPage = lazy(() =>
  import("./pages/admin/tenants/TenantDetailPage").then((m) => ({
    default: m.TenantDetailPage,
  }))
);
const TenantImportPage = lazy(() =>
  import("./pages/admin/tenants/TenantImportPage").then((m) => ({
    default: m.TenantImportPage,
  }))
);
const SuperAdminRouteGuard = lazy(() =>
  import("./components/guards/SuperAdminRouteGuard").then((m) => ({
    default: m.SuperAdminRouteGuard,
  }))
);

// Jogos Escolares
const JogosEscolaresListPage = lazy(() =>
  import("./pages/jogos-escolares/JogosEscolaresListPage").then((m) => ({
    default: m.JogosEscolaresListPage,
  }))
);
const JogoEscolarDetailPage = lazy(() =>
  import("./pages/jogos-escolares/JogoEscolarDetailPage").then((m) => ({
    default: m.JogoEscolarDetailPage,
  }))
);

function AppRouter() {
  return (
    <Router>
      <Suspense fallback={<PageSkeleton rows={8} />}>
        <Routes>
          <Route path="/" element={<RedirectToLogin />} />
          <Route
            path="/login"
            element={
              <ProtectedRoute requireAuth={false}>
                <LoginPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <ProtectedRoute requireAuth={false}>
                <ForgotPasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <ProtectedRoute requireAuth={false}>
                <ResetPasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/register"
            element={
              <ProtectedRoute requireAuth={false}>
                <RegisterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/politica-privacidade"
            element={
              <ProtectedRoute requireAuth={false}>
                <PoliticaPrivacidadePage />
              </ProtectedRoute>
            }
          />

          {/* Rotas públicas - Mapa de Escolas */}
          <Route path="/escolas/mapa/:secretariaId" element={<MapaEscolasPage />} />

          {/* Rotas públicas - Matrícula (sem autenticação) */}
          <Route path="/pesquisa/:token" element={<PesquisaPublicaPage />} />
          <Route path="/certificado/:codigo" element={<CertificadoPublicoPage />} />
          <Route
            path="/verificar-certificado-trilha/:codigo"
            element={<CertificadoTrilhaPublicoPage />}
          />
          <Route path="/verificar/formacao/:hash" element={<CertificadoFormacaoPublicoPage />} />
          <Route path="/transparencia" element={<TransparenciaPublicaPage />} />
          <Route path="/documento/:codigo" element={<DocumentoPublicoPage />} />
          <Route path="/verificar/:codigo" element={<VerificarDocumentoPage />} />
          <Route path="/matricula-publica" element={<MatriculaPublicaWizardPage />} />
          <Route path="/matricula-publica/sucesso" element={<MatriculaPublicaSuccessPage />} />
          <Route path="/matricula-publica/consulta" element={<ConsultaProtocoloPage />} />

          {/* Painel Admin - Tenants (super-admin only) */}
          <Route
            path="/admin/tenants"
            element={
              <SuperAdminRouteGuard>
                <TenantsListPage />
              </SuperAdminRouteGuard>
            }
          />
          <Route
            path="/admin/tenants/novo"
            element={
              <SuperAdminRouteGuard>
                <CreateTenantWizardPage />
              </SuperAdminRouteGuard>
            }
          />
          <Route
            path="/admin/tenants/:slug"
            element={
              <SuperAdminRouteGuard>
                <TenantDetailPage />
              </SuperAdminRouteGuard>
            }
          />
          <Route
            path="/admin/tenants/:slug/importar"
            element={
              <SuperAdminRouteGuard>
                <TenantImportPage />
              </SuperAdminRouteGuard>
            }
          />

          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SmartDashboard />} />
            <Route path="dashboard" element={<SmartDashboard />} />
            <Route
              path="dashboard/teacher"
              element={<TeacherDashboardPage />}
            />
            <Route path="dashboard/admin" element={<DashboardPage />} />
            <Route path="profile" element={<UserProfilePage />} />

            {/* Rotas de Secretarias */}
            <Route path="secretarias" element={<SecretariaListPage />} />
            <Route path="secretarias/nova" element={<SecretariaFormPage />} />
            <Route path="secretarias/:id" element={<SecretariaViewPage />} />
            <Route
              path="secretarias/:id/editar"
              element={<SecretariaFormPage />}
            />

            {/* Rotas de Escolas - Atualizadas */}
            <Route path="escolas" element={<EscolaListPage />} />
            <Route path="escolas/nova" element={<EscolaFormPage />} />
            <Route path="escolas/:id" element={<EscolaViewPage />} />
            <Route path="escolas/:id/editar" element={<EscolaFormPage />} />

            {/* Rotas de Alunos - Redirecionado para módulo pessoas */}
            <Route path="alunos" element={<AlunoListPageNew />} />
            <Route path="alunos/novo" element={<PessoaWizardPage />} />
            <Route path="alunos/:id" element={<PessoaWizardPage />} />
            <Route path="alunos/:id/editar" element={<PessoaWizardPage />} />

            {/* Rotas de Matrículas */}
            <Route path="matriculas" element={<MatriculaListPage />} />
            <Route path="matriculas/nova" element={<MatriculaFormPage />} />
            <Route path="matriculas/:id" element={<MatriculaViewPage />} />
            <Route
              path="matriculas/:id/editar"
              element={<MatriculaFormPage />}
            />

            {/* Rotas de Relatórios Importados (Designer Visual) */}
            <Route path="relatorios-importados" element={<RelatorioImportadoListPage />} />
            <Route path="relatorios-importados/:id" element={<RelatorioImportadoEditorPage />} />

            {/* Rotas de Pré-Matrículas (Admin) */}
            <Route path="matriculas/pre-matriculas/vagas" element={<PreMatriculaVagasPage />} />
            <Route path="matriculas/pre-matriculas/:id/analisar" element={<PreMatriculaAnalisePage />} />
            <Route path="matriculas/pre-matriculas/:id/efetivar" element={<PreMatriculaEfetivacaoPage />} />

            {/* Rotas de Convites */}
            <Route path="invites" element={<InviteListPage />} />
            <Route path="invites/nova" element={<InviteCreatePage />} />
            <Route path="invites/:id" element={<InviteViewPage />} />
            <Route path="invites/:id/editar" element={<InviteEditPage />} />

            {/* Rota de Suporte Interno */}
            <Route
              path="suporte-interno"
              element={<SuporteInternoPage />}
            />

            {/* Rotas de Comunicados */}
            <Route path="comunicados" element={<ComunicadoListPage />} />
            <Route path="comunicados/novo" element={<ComunicadoFormPage />} />
            <Route path="comunicados/:id" element={<ComunicadoFormPage />} />
            <Route
              path="comunicados/:id/editar"
              element={<ComunicadoFormPage />}
            />

            {/* Rotas de Campanhas */}
            <Route path="campanhas" element={<CampanhaListPage />} />
            <Route
              path="campanhas/:id/auditoria"
              element={<AuditoriaColetasPage />}
            />
            <Route path="previsao-evasao" element={<PrevisaoEvasaoPage />} />
            {/* e-Learning V2 · Área do Aluno */}
            <Route
              path="elearning/aprender"
              element={<Navigate to="/app/elearning/aprender/catalogo" replace />}
            />
            <Route path="elearning/aprender/catalogo" element={<AlunoCatalogoPage />} />
            <Route
              path="elearning/aprender/catalogo/:id"
              element={<AlunoCatalogoPage />}
            />
            <Route
              path="elearning/aprender/inscricao/:inscricaoId"
              element={<AlunoInscricaoPlayerPage />}
            />
            <Route
              path="elearning/aprender/trilhas"
              element={<AlunoTrilhaCatalogoPage />}
            />
            <Route
              path="elearning/aprender/trilhas/:id"
              element={<AlunoTrilhaDetailPage />}
            />
            <Route
              path="elearning/aprender/obrigacoes"
              element={<AlunoMinhasObrigacoesPage />}
            />
            <Route
              path="elearning/aprender/certificados"
              element={<AlunoMeusCertificadosPage />}
            />

            {/* e-Learning V1 · Redirects e páginas legadas
                (removidas na F6 após migração completa) */}
            <Route
              path="elearning"
              element={<Navigate to="/app/elearning/aprender/catalogo" replace />}
            />
            <Route
              path="elearning/curso/:inscricaoId"
              element={<RedirectLegacyElearning template="/app/elearning/aprender/inscricao/:inscricaoId" />}
            />
            <Route
              path="elearning/trilhas"
              element={<Navigate to="/app/elearning/aprender/trilhas" replace />}
            />
            <Route
              path="elearning/trilhas/:id"
              element={<RedirectLegacyElearning template="/app/elearning/aprender/trilhas/:id" />}
            />
            <Route
              path="elearning/meus-certificados"
              element={<Navigate to="/app/elearning/aprender/certificados" replace />}
            />
            <Route
              path="elearning/minhas-obrigacoes"
              element={<Navigate to="/app/elearning/aprender/obrigacoes" replace />}
            />
            {/* e-Learning V2 · Área de Autoria */}
            <Route
              path="elearning/autoria"
              element={<Navigate to="/app/elearning/autoria/meus-cursos" replace />}
            />
            <Route
              path="elearning/autoria/meus-cursos"
              element={<AutoriaMeusCursosPage />}
            />
            <Route
              path="elearning/autoria/curso/novo"
              element={<AutoriaCursoEditorPage />}
            />
            <Route
              path="elearning/autoria/curso/:id"
              element={<AutoriaCursoEditorPage />}
            />
            <Route
              path="elearning/autoria/curso/:id/alunos"
              element={<AutoriaCursoAlunosPage />}
            />

            {/* Redirects legado → autoria V2 */}
            <Route
              path="elearning/cursos/novo"
              element={<Navigate to="/app/elearning/autoria/curso/novo" replace />}
            />
            <Route
              path="elearning/cursos/:id/gerenciar"
              element={<RedirectLegacyElearning template="/app/elearning/autoria/curso/:id" />}
            />

            {/* e-Learning V2 · Área de Gestão */}
            <Route
              path="elearning/gestao"
              element={<Navigate to="/app/elearning/gestao/dashboard" replace />}
            />
            <Route
              path="elearning/gestao/dashboard"
              element={<GestaoDashboardPage />}
            />
            <Route
              path="elearning/gestao/categorias"
              element={<GestaoCategoriasPage />}
            />
            <Route
              path="elearning/gestao/compliance"
              element={<GestaoComplianceUnificadoPage />}
            />
            <Route
              path="elearning/gestao/trilhas"
              element={<GestaoTrilhasAdminPage />}
            />
            <Route
              path="elearning/gestao/template"
              element={<GestaoTemplateCertificadoPage />}
            />

            {/* Redirects legado → gestão V2 */}
            <Route
              path="elearning/template-certificado"
              element={<Navigate to="/app/elearning/gestao/template" replace />}
            />
            <Route
              path="elearning/acompanhamento"
              element={<Navigate to="/app/elearning/gestao/dashboard" replace />}
            />
            {/* P1 CODEMP */}
            <Route path="alfabetizacao" element={<DiagnosticoAlfabetizacaoPage />} />
            <Route
              path="acompanhamento-pedagogico"
              element={<AcompanhamentoPedagogicoPage />}
            />
            <Route path="avanco-estudos" element={<AvancoEstudosPage />} />
            <Route path="pei" element={<PeiPage />} />
            <Route path="alunos-duplicados" element={<AlunosDuplicadosPage />} />
            <Route
              path="transparencia-admin"
              element={<TransparenciaAdminPage />}
            />
            <Route path="campanhas/nova" element={<CampanhaFormPage />} />
            <Route path="campanhas/:id" element={<CampanhaViewPage />} />
            <Route
              path="campanhas/:id/editar"
              element={<CampanhaFormPage />}
            />
            <Route
              path="campanhas/:id/questionarios/novo"
              element={<QuestionarioFormPage />}
            />
            <Route
              path="campanhas/:id/questionarios/:qId/editar"
              element={<QuestionarioFormPage />}
            />
            <Route
              path="campanhas/:id/questionarios/:qId/responder"
              element={<ResponderQuestionarioPage />}
            />
            <Route
              path="campanhas/:id/resultados"
              element={<ResultadosPage />}
            />
            <Route
              path="campanhas/:id/comparar"
              element={<AnaliseComparativaPage />}
            />
            <Route
              path="campanhas/:id/alertas"
              element={<AlertasPage />}
            />
            {/* Rotas de Notificações */}
            <Route path="notificacoes" element={<NotificacoesPage />} />

            {/* Rotas de Descritores */}
            <Route path="descritores" element={<DescritoresListPage />} />
            <Route path="descritores/novo" element={<DescritorFormPage />} />
            <Route path="descritores/:id" element={<DescritorViewPage />} />
            <Route path="descritores/:id/editar" element={<DescritorFormPage />} />

            <Route path="permissoes" element={<PermissaoDashboardPage />} />
            <Route path="permissoes/lista" element={<PermissaoListPage />} />
            <Route path="roles" element={<RoleListPage />} />
            <Route path="roles/nova" element={<RoleCreatePage />} />
            <Route path="roles/:id" element={<RoleViewPage />} />
            <Route path="roles/:id/editar" element={<RoleEditPage />} />
            <Route path="vinculos" element={<VinculoListPage />} />
            <Route
              path="vinculos/novo"
              element={<VinculoFormPage mode="create" />}
            />
            <Route path="vinculos/:id" element={<VinculoViewPage />} />
            <Route
              path="vinculos/:id/editar"
              element={<VinculoFormPage mode="edit" />}
            />

            {/* Rotas de Usuários */}
            <Route path="usuarios" element={<UserListPage />} />
            <Route path="usuarios/:id" element={<UserDetailPage />} />

            {/* Rotas de Menus (admin) */}
            <Route path="menus" element={<MenusPage />} />
            <Route path="menus/catalogo" element={<MenuCatalogoPage />} />
            <Route path="menus/configuracao" element={<MenuConfiguracaoPage />} />

            {/* Rotas de Pessoas */}
            <Route path="pessoas" element={<PessoaListPage />} />
            <Route path="pessoas/nova" element={<PessoaWizardPage />} />
            <Route path="pessoas/:id" element={<PessoaWizardPage />} />
            <Route path="pessoas/:id/editar" element={<PessoaWizardPage />} />

            {/* Rotas de Funcionários */}
            <Route
              path="pessoas/funcionarios"
              element={<FuncionarioListPage />}
            />
            <Route
              path="pessoas/funcionarios/novo"
              element={<PessoaWizardPage />}
            />
            <Route
              path="pessoas/funcionarios/:id"
              element={<FuncionarioDetailPage />}
            />
            <Route
              path="pessoas/funcionarios/:id/editar"
              element={<PessoaWizardPage />}
            />

            {/* Rotas de Alunos */}
            <Route path="pessoas/alunos" element={<AlunoListPageNew />} />
            <Route path="pessoas/alunos/novo" element={<PessoaWizardPage />} />
            <Route path="pessoas/alunos/:id" element={<AlunoDetailPage />} />
            <Route
              path="pessoas/alunos/:id/editar"
              element={<PessoaWizardPage />}
            />

            {/* Rotas de Cargos */}
            <Route path="pessoas/cargos" element={<CargoListPage />} />
            <Route path="pessoas/cargos/novo" element={<CargoFormPage />} />
            <Route path="pessoas/cargos/:id" element={<CargoViewPage />} />
            <Route
              path="pessoas/cargos/:id/editar"
              element={<CargoFormPage />}
            />

            {/* Busca Ativa */}
            <Route path="pessoas/busca-ativa" element={<BuscaAtivaListPage />} />
            <Route path="pessoas/busca-ativa/:id" element={<BuscaAtivaViewPage />} />

            {/* GED — Gestão Eletrônica de Documentos (1 só item visível no menu: /app/ged) */}
            <Route path="ged" element={<GEDDashboardPage />} />
            <Route path="ged/buscar" element={<GEDBuscaPage />} />
            <Route path="ged/pastas" element={<GEDPastasPage />} />
            <Route path="ged/tipos" element={<GEDTiposPage />} />
            <Route path="ged/documento/:id" element={<GEDDocumentoDetailPage />} />

            {/* Termo de Autorização */}
            <Route path="pessoas/termo-autorizacao" element={<TermoAutorizacaoListPage />} />

            {/* Rotas de Estrutura Acadêmica */}
            <Route path="anos-letivos" element={<AnosLetivosPage />} />
            <Route path="anos-letivos/:id" element={<AnoLetivoViewPage />} />

            {/* Novas Rotas do Módulo Acadêmico */}
            <Route
              path="academico/disciplinas"
              element={<DisciplinaListPage />}
            />
            <Route
              path="academico/disciplinas/nova"
              element={<DisciplinaListPage />}
            />
            <Route
              path="academico/disciplinas/:id"
              element={<DisciplinaDetailPage />}
            />
            <Route
              path="academico/disciplinas/:id/editar"
              element={<DisciplinaDetailPage />}
            />

            <Route path="academico/cursos" element={<CursoListPage />} />
            <Route path="academico/cursos/novo" element={<CursoListPage />} />
            <Route path="academico/cursos/:id" element={<CursoDetailPage />} />
            <Route
              path="academico/cursos/:id/editar"
              element={<CursoDetailPage />}
            />

            <Route
              path="academico/matrizes-curriculares"
              element={<MatrizCurricularPage />}
            />
            <Route
              path="academico/matrizes-curriculares/:serieId"
              element={<MatrizCurricularPage />}
            />

            <Route
              path="academico/tipos-avaliacao"
              element={<TipoAvaliacaoListPage />}
            />
            <Route
              path="academico/tipos-avaliacao/novo"
              element={<TipoAvaliacaoListPage />}
            />
            <Route
              path="academico/tipos-avaliacao/:id"
              element={<TipoAvaliacaoListPage />}
            />
            <Route
              path="academico/tipos-avaliacao/:id/editar"
              element={<TipoAvaliacaoListPage />}
            />

            {/* Rotas de Avaliações Estruturadas - Banco de Questões */}
            <Route
              path="academico/questoes"
              element={<QuestoesListPage />}
            />
            <Route
              path="academico/questoes/nova"
              element={<QuestaoFormPage />}
            />
            <Route
              path="academico/questoes/:id"
              element={<QuestaoViewPage />}
            />
            <Route
              path="academico/questoes/:id/editar"
              element={<QuestaoFormPage />}
            />

            {/* Rotas de Avaliações Estruturadas - Avaliações */}
            <Route
              path="academico/avaliacoes"
              element={<AvaliacoesListPage />}
            />
            <Route
              path="academico/avaliacoes/nova"
              element={<AvaliacaoFormPage />}
            />
            <Route
              path="academico/avaliacoes/:id"
              element={<AvaliacaoViewPage />}
            />
            <Route
              path="academico/avaliacoes/:id/editar"
              element={<AvaliacaoFormPage />}
            />
            <Route
              path="academico/avaliacoes/:id/montar"
              element={<AvaliacaoBuilderPage />}
            />
            <Route
              path="academico/avaliacoes/:id/dashboard"
              element={<AvaliacaoDashboardPage />}
            />
            <Route
              path="academico/avaliacoes/:id/aplicar"
              element={<AvaliacaoOnlinePage />}
            />
            <Route
              path="academico/avaliacoes/:id/correcao"
              element={<CorrecaoPage />}
            />
            <Route
              path="academico/avaliacoes/:id/resultados"
              element={<ResultadosAvaliacaoPage />}
            />

            {/* Rotas de Analytics de Avaliações */}
            <Route
              path="analytics/secretaria"
              element={<DashboardSecretariaPage />}
            />
            <Route
              path="analytics/escola/:id"
              element={<DashboardEscolaPage />}
            />
            <Route
              path="analytics/turma/:id"
              element={<DashboardTurmaPage />}
            />
            <Route
              path="analytics/habilidades"
              element={<MapaHabilidadesPage />}
            />
            <Route
              path="analytics/ranking"
              element={<RankingTurmasPage />}
            />
            <Route
              path="analytics/rendimento"
              element={<RendimentoEscolarPage />}
            />
            <Route
              path="analytics/avaliacao/:id"
              element={<AvaliacaoDashboardPage />}
            />

            <Route path="academico/bncc" element={<BNCCIndexPage />} />
            <Route
              path="academico/bncc/habilidades"
              element={<HabilidadesGestaoPage />}
            />
            <Route
              path="academico/bncc/etapas"
              element={<EtapasGestaoPage />}
            />
            <Route path="academico/bncc/areas" element={<AreasGestaoPage />} />
            <Route
              path="academico/bncc/componentes"
              element={<ComponentesGestaoPage />}
            />
            <Route
              path="academico/bncc/unidades-tematicas"
              element={<UnidadesTematicasGestaoPage />}
            />
            <Route
              path="academico/bncc/objetos-conhecimento"
              element={<ObjetosConhecimentoGestaoPage />}
            />
            <Route
              path="academico/bncc/competencias-especificas"
              element={<CompetenciasEspecificasGestaoPage />}
            />
            <Route
              path="academico/bncc/campos-experiencia"
              element={<CamposExperienciaGestaoPage />}
            />
            <Route
              path="academico/bncc/objetivos-aprendizagem"
              element={<ObjetivosAprendizagemGestaoPage />}
            />

            <Route
              path="academico/alocacao-pedagogica"
              element={<AlocacaoPage />}
            />
            <Route
              path="academico/alocacao-pedagogica/:turmaId"
              element={<AlocacaoPage />}
            />

            <Route path="academico/turnos" element={<TurnosPage />} />
            <Route path="academico/turnos/novo" element={<TurnosPage />} />
            <Route path="academico/turnos/:id" element={<TurnosPage />} />
            <Route
              path="academico/turnos/:id/editar"
              element={<TurnosPage />}
            />

            <Route
              path="academico/turnos/:turnoId/periodos"
              element={<PeriodosPage />}
            />
            <Route
              path="academico/turnos/:turnoId/periodos/novo"
              element={<PeriodosPage />}
            />
            <Route
              path="academico/turnos/:turnoId/periodos/:id"
              element={<PeriodosPage />}
            />
            <Route
              path="academico/turnos/:turnoId/periodos/:id/editar"
              element={<PeriodosPage />}
            />

            <Route
              path="academico/grade-horaria"
              element={<GradeHorariaPage />}
            />
            <Route path="academico/diario" element={<DiarioIndexPage />} />
            <Route
              path="academico/grade-horaria/:turmaId"
              element={<GradeHorariaPage />}
            />
            <Route path="academico/turmas" element={<TurmaListPage />} />
            <Route path="academico/turmas/nova" element={<TurmaFormPage />} />
            <Route
              path="academico/turmas/minhas"
              element={<MinhasTurmasPage />}
            />
            <Route path="academico/turmas/:id" element={<TurmaViewPage />} />
            <Route
              path="academico/turmas/:id/editar"
              element={<TurmaFormPage />}
            />

            <Route path="academico/salas" element={<SalaListPage />} />
            <Route path="academico/salas/nova" element={<SalaListPage />} />
            <Route path="academico/salas/:id" element={<SalaListPage />} />
            <Route
              path="academico/salas/:id/editar"
              element={<SalaListPage />}
            />

            {/* Rotas de Audiência de Leitura */}
            <Route path="academico/audiencias-leitura" element={<AudienciaListPage />} />
            <Route path="academico/audiencias-leitura/nova" element={<AudienciaFormPage />} />
            <Route path="academico/audiencias-leitura/:id" element={<AudienciaViewPage />} />
            <Route
              path="academico/audiencias-leitura/:id/editar"
              element={<AudienciaFormPage />}
            />

            {/* Carteira Estudantil */}
            <Route
              path="academico/carteira-estudantil"
              element={<CarteiraEstudantilListPage />}
            />
            <Route
              path="academico/carteira-estudantil/:id"
              element={<CarteiraEstudantilViewPage />}
            />

            {/* Rotas de Monitoração */}
            <Route path="monitoracao/dashboard" element={<MonitoracaoDashboardPage />} />
            <Route path="monitoracao/tempo-real" element={<MonitoracaoTempoRealPage />} />
            <Route path="cameras" element={<CamerasPage />} />

            {/* Rotas de Recursos Humanos (RH) — Licitação Cascavel/CE */}
            <Route path="rh" element={<RHModulePage />} />
            <Route path="folha-ponto" element={<MeuPontoPage />} />

            {/* Rotas de Formação de Servidores */}
            <Route path="formacao/servidores" element={<FormacaoServidoresListPage />} />
            <Route path="formacao/servidores/nova" element={<FormacaoServidoresFormPage />} />
            <Route path="formacao/servidores/:id" element={<FormacaoServidoresViewPage />} />
            <Route path="formacao/minhas-inscricoes" element={<FormacaoMinhasInscricoesPage />} />
            <Route path="formacao/minhas-inscricoes/:id" element={<FormacaoServidorDetalhePublicoPage />} />

            {/* Rotas do Módulo Formador */}
            <Route path="formacao/formadores" element={<FormadorModulePage />} />
            <Route path="formacao/formadores/dashboard" element={<Navigate to="/app/formacao/formadores" replace />} />
            <Route path="formacao/formadores/:id" element={<FormadorDetailPage />} />
            <Route path="formacao/continuada/:id" element={<FormacaoContinuadaDetailPage />} />
            <Route path="formacao/guias/:id" element={<GuiaPlanejamentoBuilderPage />} />

            {/* Rotas de Gestão de Processos (Protocolo) */}
            <Route path="gestao-processos" element={<ProcessoListPage />} />
            <Route path="gestao-processos/novo" element={<ProcessoFormPage />} />
            <Route path="gestao-processos/:id" element={<ProcessoViewPage />} />

            {/* Rotas do Conselho Escolar */}
            <Route path="conselho-escolar" element={<ConselhoEscolarListPage />} />
            <Route path="conselho-escolar/:id" element={<ConselhoEscolarDetailPage />} />

            {/* Rotas do Diário AEE */}
            <Route path="diario-aee" element={<DiarioAEEListPage />} />
            <Route path="diario-aee/:id" element={<DiarioAEEDetailPage />} />

            {/* Rotas do Financeiro */}
            <Route path="financeiro" element={<FinanceiroDashboardPage />} />
            <Route path="financeiro/planos-aplicacao" element={<PlanosAplicacaoPage />} />
            <Route path="financeiro/planos-aplicacao/:id" element={<PlanoAplicacaoDetailPage />} />
            <Route path="financeiro/empenhos" element={<EmpenhosPage />} />
            <Route path="financeiro/empenhos/:id" element={<EmpenhoDetailPage />} />
            <Route path="financeiro/prestacoes-contas/:id" element={<PrestacaoContasDetailPage />} />

            {/* Rotas do Patrimônio */}
            <Route path="patrimonio" element={<PatrimonioDashboardPage />} />
            <Route path="patrimonio/bens/:id" element={<BemPatrimonialDetailPage />} />
            <Route path="patrimonio/inventarios/:id" element={<InventarioDetailPage />} />
            <Route path="patrimonio/requisicoes/:id" element={<RequisicaoMaterialDetailPage />} />

            {/* Rotas do Educacenso (Censo Escolar INEP/MEC) */}
            <Route path="educacenso" element={<EducacensoIndexPage />} />
            <Route path="educacenso/gerar" element={<EducacensoGerarPage />} />
            <Route path="educacenso/importar" element={<EducacensoImportarPage />} />
            <Route path="educacenso/validar" element={<EducacensoValidarPage />} />
            <Route path="educacenso/jobs/:id" element={<EducacensoJobDetailPage />} />

            {/* Rotas de Transporte Escolar */}
            <Route path="transportes" element={<TransporteDashboardPage />} />
            <Route path="transportes/veiculos" element={<VeiculoListPage />} />
            <Route path="transportes/veiculos/novo" element={<VeiculoFormPage />} />
            <Route path="transportes/veiculos/:id" element={<VeiculoViewPage />} />
            <Route path="transportes/veiculos/:id/editar" element={<VeiculoFormPage />} />
            <Route path="transportes/motoristas" element={<MotoristaListPage />} />
            <Route path="transportes/motoristas/novo" element={<MotoristaFormPage />} />
            <Route path="transportes/motoristas/:id" element={<MotoristaViewPage />} />
            <Route path="transportes/motoristas/:id/editar" element={<MotoristaFormPage />} />
            <Route path="transportes/monitores" element={<MonitorListPage />} />
            <Route path="transportes/monitores/novo" element={<MonitorFormPage />} />
            <Route path="transportes/monitores/:id" element={<MonitorViewPage />} />
            <Route path="transportes/monitores/:id/editar" element={<MonitorFormPage />} />
            <Route path="transportes/contratos" element={<ContratoListPage />} />
            <Route path="transportes/contratos/novo" element={<ContratoFormPage />} />
            <Route path="transportes/contratos/:id" element={<ContratoViewPage />} />
            <Route path="transportes/contratos/:id/editar" element={<ContratoFormPage />} />
            <Route path="transportes/pontos-embarque" element={<PontoEmbarqueListPage />} />
            <Route path="transportes/pontos-embarque/novo" element={<PontoEmbarqueFormPage />} />
            <Route path="transportes/pontos-embarque/:id" element={<PontoEmbarqueViewPage />} />
            <Route path="transportes/pontos-embarque/:id/editar" element={<PontoEmbarqueFormPage />} />
            <Route path="transportes/rotas" element={<RotaListPage />} />
            <Route path="transportes/rotas/nova" element={<RotaFormPage />} />
            <Route path="transportes/rotas/:id" element={<RotaViewPage />} />
            <Route path="transportes/rotas/:id/editar" element={<RotaFormPage />} />
            <Route path="transportes/execucoes" element={<ExecucaoRotaListPage />} />
            <Route path="transportes/execucoes/nova" element={<ExecucaoRotaFormPage />} />
            <Route path="transportes/execucoes/:id" element={<ExecucaoRotaViewPage />} />
            <Route path="transportes/execucoes/:id/editar" element={<ExecucaoRotaFormPage />} />
            <Route path="transportes/alunos-transporte" element={<AlunoTransporteListPage />} />
            <Route path="transportes/alunos-transporte/novo" element={<AlunoTransporteFormPage />} />
            <Route path="transportes/alunos-transporte/:id" element={<AlunoTransporteViewPage />} />
            <Route path="transportes/alunos-transporte/:id/editar" element={<AlunoTransporteFormPage />} />
            <Route path="transportes/manutencoes" element={<ManutencaoListPage />} />
            <Route path="transportes/manutencoes/novo" element={<ManutencaoFormPage />} />
            <Route path="transportes/manutencoes/:id" element={<ManutencaoViewPage />} />
            <Route path="transportes/manutencoes/:id/editar" element={<ManutencaoFormPage />} />
            <Route path="transportes/abastecimentos" element={<AbastecimentoListPage />} />
            <Route path="transportes/abastecimentos/novo" element={<AbastecimentoFormPage />} />
            <Route path="transportes/abastecimentos/:id" element={<AbastecimentoViewPage />} />
            <Route path="transportes/abastecimentos/:id/editar" element={<AbastecimentoFormPage />} />
            <Route path="transportes/ocorrencias" element={<OcorrenciaListPage />} />
            <Route path="transportes/ocorrencias/novo" element={<OcorrenciaFormPage />} />
            <Route path="transportes/ocorrencias/:id" element={<OcorrenciaViewPage />} />
            <Route path="transportes/ocorrencias/:id/editar" element={<OcorrenciaFormPage />} />
            <Route path="transportes/solicitacoes" element={<SolicitacaoListPage />} />
            <Route path="transportes/solicitacoes/novo" element={<SolicitacaoFormPage />} />
            <Route path="transportes/solicitacoes/:id" element={<SolicitacaoViewPage />} />
            <Route path="transportes/solicitacoes/:id/editar" element={<SolicitacaoFormPage />} />
            <Route path="transportes/despesas" element={<DespesaListPage />} />
            <Route path="transportes/despesas/novo" element={<DespesaFormPage />} />
            <Route path="transportes/despesas/:id" element={<DespesaViewPage />} />
            <Route path="transportes/despesas/:id/editar" element={<DespesaFormPage />} />
            <Route path="transportes/rastreamento" element={<RastreamentoGpsPage />} />
            <Route path="transportes/configuracoes" element={<ConfiguracaoTransportePage />} />

            {/* Fardamento (Uniformes) */}
            <Route path="fardamentos" element={<FardamentoListPage />} />
            <Route path="fardamentos/novo" element={<FardamentoFormPage />} />
            <Route path="fardamentos/:id" element={<FardamentoViewPage />} />
            <Route path="fardamentos/:id/editar" element={<FardamentoFormPage />} />

            {/* Biblioteca Escolar */}
            <Route path="biblioteca" element={<BibliotecaDashboardPage />} />
            <Route path="biblioteca/configuracao" element={<BibliotecaConfigPage />} />
            <Route path="biblioteca/obras" element={<ObrasListPage />} />
            <Route path="biblioteca/obras/nova" element={<ObraFormPage />} />
            <Route path="biblioteca/obras/:id" element={<ObraViewPage />} />
            <Route path="biblioteca/obras/:id/editar" element={<ObraFormPage />} />
            <Route path="biblioteca/autores" element={<AutoresListPage />} />
            <Route path="biblioteca/autores/novo" element={<AutorFormPage />} />
            <Route path="biblioteca/autores/:id/editar" element={<AutorFormPage />} />
            <Route path="biblioteca/editoras" element={<EditorasListPage />} />
            <Route path="biblioteca/editoras/nova" element={<EditoraFormPage />} />
            <Route path="biblioteca/editoras/:id/editar" element={<EditoraFormPage />} />
            <Route path="biblioteca/categorias" element={<CategoriasListPage />} />
            <Route path="biblioteca/categorias/nova" element={<CategoriaFormPage />} />
            <Route path="biblioteca/categorias/:id/editar" element={<CategoriaFormPage />} />
            <Route path="biblioteca/etiquetas" element={<EtiquetasListPage />} />
            <Route path="biblioteca/etiquetas/nova" element={<EtiquetaFormPage />} />
            <Route path="biblioteca/etiquetas/:id/editar" element={<EtiquetaFormPage />} />
            <Route path="biblioteca/exemplares" element={<ExemplaresListPage />} />
            <Route path="biblioteca/exemplares/novo" element={<ExemplarFormPage />} />
            <Route path="biblioteca/exemplares/:id/editar" element={<ExemplarFormPage />} />
            {/* Biblioteca - Circulação (Fase 2) */}
            <Route path="biblioteca/emprestimos" element={<EmprestimosListPage />} />
            <Route path="biblioteca/emprestimos/novo" element={<EmprestimoFormPage />} />
            <Route path="biblioteca/emprestimos/:id" element={<EmprestimoViewPage />} />
            <Route path="biblioteca/reservas" element={<ReservasListPage />} />
            <Route path="biblioteca/reservas/nova" element={<ReservaFormPage />} />
            <Route path="biblioteca/usuarios" element={<UsuariosListPage />} />
            <Route path="biblioteca/usuarios/novo" element={<UsuarioFormPage />} />
            <Route path="biblioteca/usuarios/:id/editar" element={<UsuarioFormPage />} />
            {/* Biblioteca - Financeiro (Fase 3) */}
            <Route path="biblioteca/multas" element={<MultasListPage />} />
            <Route path="biblioteca/multas/:id" element={<MultaViewPage />} />
            {/* Biblioteca - Patrimônio (Fase 4) */}
            <Route path="biblioteca/inventarios" element={<InventariosListPage />} />
            <Route path="biblioteca/inventarios/novo" element={<InventarioFormPage />} />
            <Route path="biblioteca/inventarios/:id" element={<InventarioViewPage />} />
            <Route path="biblioteca/inventarios/:id/editar" element={<InventarioFormPage />} />
            <Route path="biblioteca/aquisicoes" element={<AquisicoesListPage />} />
            <Route path="biblioteca/aquisicoes/nova" element={<AquisicaoFormPage />} />
            <Route path="biblioteca/aquisicoes/:id/editar" element={<AquisicaoFormPage />} />
            <Route path="biblioteca/doacoes" element={<DoacoesListPage />} />
            <Route path="biblioteca/doacoes/nova" element={<DoacaoFormPage />} />
            <Route path="biblioteca/doacoes/:id/editar" element={<DoacaoFormPage />} />
            <Route path="biblioteca/baixas" element={<BaixasListPage />} />
            <Route path="biblioteca/baixas/nova" element={<BaixaFormPage />} />
            <Route path="biblioteca/movimentacoes" element={<MovimentacoesListPage />} />

            {/* Biblioteca - Visão do Aluno */}
            <Route path="biblioteca-alunos" element={<AlunoBibliotecaPage />} />

            {/* Gestão de Obras */}
            <Route path="infraestrutura/obras" element={<ObrasIndexPage />} />
            {/* Gestão de Obras - Obra Principal */}
            <Route path="infraestrutura/obras/lista" element={<ObrasConstrucaoListPage />} />
            <Route path="infraestrutura/obras/nova" element={<ObraConstrucaoFormPage />} />
            <Route path="infraestrutura/obras/:obraId" element={<ObraConstrucaoViewPage />}>
              <Route index element={<Navigate to="resumo" replace />} />
              <Route path="resumo" element={<ObraResumoTab />} />
              <Route path="projetos" element={<ObraProjetosTab />} />
              <Route path="cronograma" element={<ObraCronogramaTab />} />
              <Route path="orcamento" element={<ObraOrcamentoTab />} />
              <Route path="contratos" element={<ObraContratosTab />} />
              <Route path="medicoes" element={<ObraMedicoesTab />} />
              <Route path="financeiro" element={<ObraFinanceiroTab />} />
              <Route path="execucao" element={<ObraExecucaoTab />} />
              <Route path="diario" element={<ObraDiarioTab />} />
              <Route path="fotos" element={<ObraFotosTab />} />
              <Route path="estoque" element={<ObraEstoqueTab />} />
              <Route path="equipamentos" element={<ObraEquipamentosTab />} />
              <Route path="funcionarios" element={<ObraFuncionariosTab />} />
              <Route path="vistorias" element={<ObraVistoriasTab />} />
              <Route path="nao-conformidades" element={<ObraNaoConformidadesTab />} />
              <Route path="acidentes" element={<ObraAcidentesTab />} />
              <Route path="documentos" element={<ObraDocumentosTab />} />
              <Route path="relatorios" element={<ObraRelatoriosTab />} />
            </Route>
            <Route path="infraestrutura/obras/dashboard" element={<ObrasDashboardPage />} />
            <Route path="infraestrutura/obras/alertas/medicoes-pendentes" element={<MedicoesPendentesPage />} />
            <Route path="infraestrutura/obras/alertas/nao-conformidades" element={<NaoConformidadesAbertasPage />} />
            <Route path="infraestrutura/obras/alertas/documentos-vencendo" element={<DocumentosVencendoPage />} />
            <Route path="infraestrutura/obras/:obraId/editar" element={<ObraConstrucaoFormPage />} />
            {/* Gestão de Obras - Cadastros Base */}
            <Route path="infraestrutura/obras/cadastros/tipos-obra" element={<TiposObraListPage />} />
            <Route path="infraestrutura/obras/cadastros/tipos-obra/novo" element={<TipoObraFormPage />} />
            <Route path="infraestrutura/obras/cadastros/tipos-obra/:id/editar" element={<TipoObraFormPage />} />
            <Route path="infraestrutura/obras/cadastros/fiscais" element={<FiscaisListPage />} />
            <Route path="infraestrutura/obras/cadastros/fiscais/novo" element={<FiscalFormPage />} />
            <Route path="infraestrutura/obras/cadastros/fiscais/:id/editar" element={<FiscalFormPage />} />
            <Route path="infraestrutura/obras/cadastros/empreiteiras" element={<EmpreiteirasListPage />} />
            <Route path="infraestrutura/obras/cadastros/empreiteiras/nova" element={<EmpreiteiraFormPage />} />
            <Route path="infraestrutura/obras/cadastros/empreiteiras/:id" element={<EmpreiteiraViewPage />} />
            <Route path="infraestrutura/obras/cadastros/empreiteiras/:id/editar" element={<EmpreiteiraFormPage />} />
            <Route path="infraestrutura/obras/cadastros/materiais" element={<MateriaisObraListPage />} />
            <Route path="infraestrutura/obras/cadastros/materiais/novo" element={<MaterialObraFormPage />} />
            <Route path="infraestrutura/obras/cadastros/materiais/:id/editar" element={<MaterialObraFormPage />} />
            <Route path="infraestrutura/obras/cadastros/fornecedores" element={<FornecedoresObraListPage />} />
            <Route path="infraestrutura/obras/cadastros/fornecedores/novo" element={<FornecedorObraFormPage />} />
            <Route path="infraestrutura/obras/cadastros/fornecedores/:id/editar" element={<FornecedorObraFormPage />} />
            <Route path="infraestrutura/obras/cadastros/configuracoes" element={<ConfiguracoesObraPage />} />

            {/* Merenda Escolar */}
            <Route path="merenda" element={<MerendaIndexPage />} />
            <Route path="merenda/cozinhas" element={<CozinhasListPage />} />
            <Route path="merenda/cozinhas/nova" element={<CozinhaFormPage />} />
            <Route path="merenda/cozinhas/:id/editar" element={<CozinhaFormPage />} />
            <Route path="merenda/grupos-alimentares" element={<GruposAlimentaresListPage />} />
            <Route path="merenda/grupos-alimentares/novo" element={<GrupoAlimentarFormPage />} />
            <Route path="merenda/grupos-alimentares/:id/editar" element={<GrupoAlimentarFormPage />} />
            <Route path="merenda/unidades-medida" element={<UnidadesMedidaListPage />} />
            <Route path="merenda/unidades-medida/nova" element={<UnidadeMedidaFormPage />} />
            <Route path="merenda/unidades-medida/:id/editar" element={<UnidadeMedidaFormPage />} />
            <Route path="merenda/alimentos" element={<AlimentosListPage />} />
            <Route path="merenda/alimentos/novo" element={<AlimentoFormPage />} />
            <Route path="merenda/alimentos/:id/editar" element={<AlimentoFormPage />} />
            <Route path="merenda/tipos-refeicao" element={<TiposRefeicaoListPage />} />
            <Route path="merenda/tipos-refeicao/novo" element={<TipoRefeicaoFormPage />} />
            <Route path="merenda/tipos-refeicao/:id/editar" element={<TipoRefeicaoFormPage />} />
            <Route path="merenda/receitas" element={<ReceitasListPage />} />
            <Route path="merenda/receitas/nova" element={<ReceitaFormPage />} />
            <Route path="merenda/receitas/:id/editar" element={<ReceitaFormPage />} />
            <Route path="merenda/cardapios" element={<CardapiosListPage />} />
            <Route path="merenda/cardapios/novo" element={<CardapioFormPage />} />
            <Route path="merenda/cardapios/:id/editar" element={<CardapioFormPage />} />
            <Route path="merenda/estoque" element={<EstoquesListPage />} />
            <Route path="merenda/estoque/novo" element={<EstoqueFormPage />} />
            <Route path="merenda/estoque/:id" element={<EstoqueDetailPage />} />
            <Route path="merenda/estoque/:id/editar" element={<EstoqueFormPage />} />
            <Route path="merenda/fornecedores" element={<FornecedoresListPage />} />
            <Route path="merenda/fornecedores/novo" element={<FornecedorFormPage />} />
            <Route path="merenda/fornecedores/:id/editar" element={<FornecedorFormPage />} />
            <Route path="merenda/contratos" element={<MerendaContratosListPage />} />
            <Route path="merenda/contratos/novo" element={<MerendaContratoFormPage />} />
            <Route path="merenda/contratos/:id/editar" element={<MerendaContratoFormPage />} />
            <Route path="merenda/pedidos-compra" element={<PedidosCompraListPage />} />
            <Route path="merenda/pedidos-compra/novo" element={<PedidoCompraFormPage />} />
            <Route path="merenda/pedidos-compra/:id" element={<PedidoCompraDetailPage />} />
            <Route path="merenda/pedidos-compra/:id/editar" element={<PedidoCompraFormPage />} />
            <Route path="merenda/entregas" element={<EntregasListPage />} />
            <Route path="merenda/entregas/nova" element={<EntregaFormPage />} />
            <Route path="merenda/entregas/:id" element={<EntregaDetailPage />} />
            <Route path="merenda/entregas/:id/editar" element={<EntregaFormPage />} />
            <Route path="merenda/refeicoes-servidas" element={<RefeicoesServidasListPage />} />
            <Route path="merenda/refeicoes-servidas/nova" element={<RefeicaoServidaFormPage />} />
            <Route path="merenda/refeicoes-servidas/:id" element={<RefeicaoServidaDetailPage />} />
            <Route path="merenda/refeicoes-servidas/:id/editar" element={<RefeicaoServidaFormPage />} />
            <Route path="merenda/producao-diaria" element={<ProducoesDiariasListPage />} />
            <Route path="merenda/producao-diaria/nova" element={<ProducaoDiariaFormPage />} />
            <Route path="merenda/producao-diaria/:id" element={<ProducaoDiariaDetailPage />} />
            <Route path="merenda/producao-diaria/:id/editar" element={<ProducaoDiariaFormPage />} />
            {/* Fase 8 - Escalas de Trabalho */}
            <Route path="merenda/escalas-trabalho" element={<EscalasTrabalhoListPage />} />
            <Route path="merenda/escalas-trabalho/nova" element={<EscalaTrabalhoFormPage />} />
            <Route path="merenda/escalas-trabalho/:id/editar" element={<EscalaTrabalhoFormPage />} />
            {/* Fase 9 - Dietas Especiais */}
            <Route path="merenda/dietas-especiais/tipos" element={<DietasEspeciaisListPage />} />
            <Route path="merenda/dietas-especiais/tipos/nova" element={<DietaEspecialFormPage />} />
            <Route path="merenda/dietas-especiais/tipos/:id/editar" element={<DietaEspecialFormPage />} />
            <Route path="merenda/dietas-especiais/alunos" element={<AlunosDietasListPage />} />
            <Route path="merenda/dietas-especiais/alunos/novo" element={<AlunoDietaFormPage />} />
            <Route path="merenda/dietas-especiais/alunos/:id" element={<AlunoDietaDetailPage />} />
            <Route path="merenda/dietas-especiais/alunos/:id/editar" element={<AlunoDietaFormPage />} />
            {/* Fase 10 - Equipamentos */}
            <Route path="merenda/equipamentos" element={<EquipamentosMerendaListPage />} />
            <Route path="merenda/equipamentos/novo" element={<EquipamentoMerendaFormPage />} />
            <Route path="merenda/equipamentos/:id" element={<EquipamentoMerendaDetailPage />} />
            <Route path="merenda/equipamentos/:id/editar" element={<EquipamentoMerendaFormPage />} />
            {/* Fase 11 - Financeiro */}
            <Route path="merenda/financeiro/despesas" element={<DespesasMerendaListPage />} />
            <Route path="merenda/financeiro/despesas/nova" element={<DespesaMerendaFormPage />} />
            <Route path="merenda/financeiro/despesas/:id" element={<DespesaMerendaDetailPage />} />
            <Route path="merenda/financeiro/despesas/:id/editar" element={<DespesaMerendaFormPage />} />
            <Route path="merenda/financeiro/orcamento" element={<OrcamentosMerendaListPage />} />
            <Route path="merenda/financeiro/orcamento/novo" element={<OrcamentoMerendaFormPage />} />
            <Route path="merenda/financeiro/orcamento/:id" element={<OrcamentoMerendaDetailPage />} />
            <Route path="merenda/financeiro/orcamento/:id/editar" element={<OrcamentoMerendaFormPage />} />
            {/* Fase 12 - Qualidade e Configurações */}
            <Route path="merenda/aceitabilidade" element={<AvaliacoesAceitabilidadeListPage />} />
            <Route path="merenda/aceitabilidade/nova" element={<AvaliacaoAceitabilidadeFormPage />} />
            <Route path="merenda/aceitabilidade/:id" element={<AvaliacaoAceitabilidadeDetailPage />} />
            <Route path="merenda/aceitabilidade/:id/editar" element={<AvaliacaoAceitabilidadeFormPage />} />
            <Route path="merenda/configuracoes" element={<ConfiguracoesMerendaListPage />} />
            <Route path="merenda/configuracoes/nova" element={<ConfiguracaoMerendaFormPage />} />
            <Route path="merenda/configuracoes/:id/editar" element={<ConfiguracaoMerendaFormPage />} />

            {/* Fase 13 - Analytics e Dashboards */}
            <Route path="merenda/analytics/dashboard" element={<MerendaDashboardPage />} />
            <Route path="merenda/analytics/estoque" element={<EstoqueReportPage />} />
            <Route path="merenda/analytics/vencimento" element={<VencimentoReportPage />} />
            <Route path="merenda/analytics/refeicoes" element={<RefeicoesReportPage />} />
            <Route path="merenda/analytics/custos" element={<CustosReportPage />} />
            <Route path="merenda/analytics/pnae" element={<PNAEReportPage />} />
            <Route path="merenda/analytics/aceitabilidade" element={<AceitabilidadeReportPage />} />
            <Route path="merenda/analytics/desperdicio" element={<DesperdicioReportPage />} />
            <Route path="merenda/analytics/cardapios" element={<CardapiosReportPage />} />

            {/* Rotas de Professor - Visão de Turma */}
            <Route path="turmas/:id" element={<TeacherClassView />} />
            <Route
              path="turmas/:id/frequencia"
              element={<TeacherClassView />}
            />
            <Route
              path="turmas/:id/frequencia/detalhes"
              element={<FrequenciaTurmaPage />}
            />
            <Route path="turmas/:id/notas" element={<TeacherClassView />} />
            <Route
              path="turmas/:id/planos-aula"
              element={<PlanoAulaListPage />}
            />
            <Route
              path="turmas/:id/planos-aula/novo"
              element={<PlanoAulaFormPage />}
            />
            <Route
              path="turmas/:id/planos-aula/:planoId/editar"
              element={<PlanoAulaFormPage />}
            />
            <Route
              path="turmas/:id/planos-aula/:planoId"
              element={<PlanoAulaViewPage />}
            />
            <Route path="turmas/:id/alunos" element={<TeacherClassView />} />
            <Route
              path="turmas/:id/comunicados"
              element={<TeacherClassView />}
            />
            <Route
              path="turmas/:id/relatorios"
              element={<TeacherClassView />}
            />
            <Route
              path="turmas/:id/configuracoes"
              element={<TeacherClassView />}
            />

            {/* Rotas de Benefícios Sociais */}
            <Route path="beneficios-sociais" element={<BeneficiosSociaisPage />} />
            <Route path="beneficios-sociais/:id/alunos" element={<BeneficioAlunosPage />} />

            {/* Jogos Escolares */}
            <Route path="jogos-escolares" element={<JogosEscolaresListPage />} />
            <Route path="jogos-escolares/:id" element={<JogoEscolarDetailPage />} />

            {/* Auditoria do Sistema */}
            <Route path="auditoria" element={<AuditLogPage />} />

            {/* Rotas do AVA - Ambiente Virtual de Aprendizagem (Professor/Admin) */}
            <Route path="ambiente-virtualizado" element={<AvaListPage />} />
            <Route path="ambiente-virtualizado/:turmaId" element={<AvaPage />} />

            {/* Rotas do Aluno - Guard protege as páginas, Layout unificado */}
            <Route path="aluno" element={<AlunoGuard><Outlet /></AlunoGuard>}>
              <Route index element={<AlunoDashboardPage />} />
              <Route path="notas" element={<AlunoNotasPage />} />
              <Route path="frequencia" element={<AlunoFrequenciaPage />} />
              <Route path="horario" element={<AlunoHorarioPage />} />
              <Route path="diario" element={<AlunoDiarioClassePage />} />
              <Route path="agenda" element={<AlunoAgendaPage />} />
              <Route path="audiencia-leitura" element={<AlunoAudienciaLeituraPage />} />
              <Route path="avaliacoes" element={<AlunoAvaliacoesPage />} />
              <Route path="documentos" element={<AlunoDocumentosPage />} />
              <Route path="transporte" element={<AlunoTransportePage />} />
              <Route path="carteira-estudantil" element={<AlunoCarteiraEstudantilPage />} />
              <Route path="perfil" element={<AlunoPerfilPage />} />
              <Route path="ava/:turmaId" element={<AvaAlunoPage />} />
            </Route>

            {/* Rotas do Responsável - Guard protege as páginas, Layout unificado */}
            <Route path="responsavel" element={<ResponsavelGuard><Outlet /></ResponsavelGuard>}>
              <Route index element={<ResponsavelDashboardPage />} />
              <Route path="perfil" element={<ResponsavelPerfilPage />} />
              <Route path="aluno/:alunoId" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/dados" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/ficha-medica" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/frequencia" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/notas" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/meritos" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/fotos" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/agenda" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/audiencia-leitura" element={<ResponsavelAlunoDetailPage />} />
              <Route path="aluno/:alunoId/frequencia-facial" element={<ResponsavelFrequenciaFacialPage />} />
              <Route path="aluno/:alunoId/pre-matricula" element={<PreMatriculaListPage />} />

              {/* Rotas de Pré-Matrícula (Responsável) */}
              <Route path="pre-matricula" element={<PreMatriculaListPage />} />
              <Route path="pre-matricula/nova" element={<PreMatriculaWizardPage />} />
              <Route path="pre-matricula/sucesso" element={<PreMatriculaSuccessPage />} />
              <Route path="pre-matricula/:id" element={<PreMatriculaDetailPage />} />
            </Route>
          </Route>

          {/* Rota catch-all — deve ser a última */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default AppRouter;
